import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { yearsSince } from '../../utils/timeInBusiness';
import { useLanguage } from '../../context/LanguageContext';
import {
  getClientById, getRepById, getRequestsByClient,
  getActivityByClient, getMissingCategories, DOC_CATEGORIES
} from '../../data/mockData';
import FolderTree from '../../components/shared/FolderTree';
import DocumentTable from '../../components/shared/DocumentTable';
import UploadZone from '../../components/shared/UploadZone';
import StatusBadge from '../../components/shared/StatusBadge';
import ZohoLink from '../../components/ZohoLink';
import { isSuperAdmin } from '../../data/superAdmins';
import {
  ArrowLeft, Mail, Phone, MapPin, Building2, UserCheck,
  AlertCircle, Clock, Upload, Send, CheckCircle2, X, Bell, Plus, ChevronRight, ChevronDown, Trash2, RefreshCw, Search
} from 'lucide-react';
import { matchesClientSearch } from '../../utils/clientSearch';
import { lazy, Suspense } from 'react';
const AutoSubmit = lazy(() => import('./AutoSubmit'));
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar } from 'recharts';

const API = process.env.REACT_APP_API_URL || 'https://api.orbit-technology.com/api';
// Power Automate flow behind the Ping button on the Documents tab. Posted to
// straight from the browser, the same way the lender flows in AutoSubmit are.
// Left blank the button still shows but reports that Ping isn't set up yet.
const PING_MATT_URL = 'https://default87067de17bff468994aa610cdb27ba.92.environment.api.powerplatform.com:443/powerautomate/automations/direct/cu/19/workflows/ba9d5592b2e543db9f19c437d1f98d4a/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=EsYo0OniU5JtfWSIB9pEo8cfU7zf5fFXzcE0Z7dyj3E';
const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || 'https://www.orbit-technology.com';

// ─── Inline lender breakdown with click-to-expand ─────────────────────────────
function LenderBreakdownInline({ lenders }) {
  const [expanded, setExpanded] = useState(null);

  // Group lenders
  const grouped = useMemo(() => {
    const map = {};
    lenders.forEach(l => {
      const name = l['Detected Lender'] || l.lender || 'Unknown';
      if (!map[name]) map[name] = { name, total: 0, txns: [] };
      const amt = Math.abs(l['Lender Debit Amount'] || l.amount || 0);
      map[name].total += amt;
      map[name].txns.push({
        date: l.date || '',
        desc: l.description || l.keyword || name,
        amount: amt,
        type: l.type || 'debit',
        statement: l.statement || '',
      });
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [lenders]);

  return (
    <div>
      <p className="text-xs text-gray-400 mb-2">Detected Lenders — click for breakdown</p>
      <div className="flex flex-wrap gap-2">
        {grouped.map(g => (
          <button key={g.name} onClick={() => setExpanded(expanded === g.name ? null : g.name)}
            className={`px-2.5 py-1 text-xs rounded-lg border transition-all ${
              expanded === g.name
                ? 'bg-purple-100 text-purple-800 border-purple-300 ring-2 ring-purple-200'
                : 'bg-purple-50 text-purple-700 border-purple-100 hover:bg-purple-100'
            }`}>
            {g.name} — ${Number(g.total).toLocaleString()}
          </button>
        ))}
      </div>
      {expanded && (() => {
        const g = grouped.find(x => x.name === expanded);
        if (!g) return null;
        return (
          <div className="mt-3 bg-gray-50 border border-gray-200 rounded-xl p-4 animate-in fade-in">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">{g.name}</p>
                <p className="text-xs text-gray-400">{g.txns.length} transaction{g.txns.length !== 1 ? 's' : ''} · ${Number(g.total).toLocaleString()} total</p>
              </div>
              <button onClick={() => setExpanded(null)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-1.5 px-2 text-gray-400 font-medium">Date</th>
                    <th className="text-left py-1.5 px-2 text-gray-400 font-medium">Description</th>
                    <th className="text-right py-1.5 px-2 text-gray-400 font-medium">Amount</th>
                    <th className="text-left py-1.5 px-2 text-gray-400 font-medium">Statement</th>
                  </tr>
                </thead>
                <tbody>
                  {g.txns.sort((a, b) => (a.date || '').localeCompare(b.date || '')).map((t, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-1.5 px-2 text-gray-700 font-mono">{t.date || '—'}</td>
                      <td className="py-1.5 px-2 text-gray-700 max-w-[200px] truncate" title={t.desc}>{t.desc}</td>
                      <td className="py-1.5 px-2 text-right font-bold text-amber-600">${Number(t.amount).toLocaleString()}</td>
                      <td className="py-1.5 px-2 text-gray-400 truncate max-w-[100px]">{t.statement || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── Helpers for underwriting timeline ────────────────────────────────────────
function extractPeriod(fileName) {
  // Try to extract date from filename like "20260331-statements-1746-.pdf"
  const m = fileName.match(/(\d{4})(\d{2})(\d{2})/);
  if (m) {
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[parseInt(m[2],10)-1]} ${m[1]}`;
  }
  // Try "October 2025" style
  const m2 = fileName.match(/(January|February|March|April|May|June|July|August|September|October|November|December)\s*(\d{4})/i);
  if (m2) return `${m2[1].slice(0,3)} ${m2[2]}`;
  return fileName.replace(/\.pdf$/i,'').slice(0,12);
}

const MONTH_IDX = { jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,oct:9,nov:10,dec:11 };
function periodSortKey(p) {
  // "Mar 2026" → 202602, "Apr 2026" → 202603
  const m = (p || '').match(/^([A-Za-z]{3})\s*(\d{4})/);
  if (m) return parseInt(m[2]) * 100 + (MONTH_IDX[m[1].toLowerCase()] ?? 0);
  return 0;
}

function buildTimeline(bankStatements) {
  return bankStatements.map(d => {
    const f = d.extractedFinancials;
    return {
      period: extractPeriod(d.fileName),
      fileName: d.fileName,
      credits: f.credits || 0,
      debits: f.debits || 0,
      cashFlow: f.cash_flow ?? ((f.credits||0) - (f.debits||0)),
      lenderDebits: f.lender_debits || 0,
      avgDailyBalance: f.avg_daily_balance || 0,
      nsfCount: f.nsf_count || 0,
      withholdingRate: f.withholding_rate || 0,
      lenders: f.lender_list || [],
    };
  }).sort((a, b) => periodSortKey(a.period) - periodSortKey(b.period));
}

// ─── UnderwritingTab Component ────────────────────────────────────────────────
function UnderwritingTab({ id, token, headers, realDocs, docFinancials, statementTimeline, ocrResults, setOcrResults, ocrSending, setOcrSending, ocrStatus, setOcrStatus }) {
  const [expandedLender, setExpandedLender] = useState(null);

  // Build per-lender monthly breakdown from timeline
  const lenderMonthly = useMemo(() => {
    if (!statementTimeline.length) return {};
    const map = {};
    statementTimeline.forEach(s => {
      const perLender = s.lenders.length > 0 ? s.lenderDebits / s.lenders.length : 0;
      s.lenders.forEach(name => {
        const key = name.toUpperCase().trim();
        if (!map[key]) map[key] = [];
        map[key].push({ period: s.period, amount: Math.round(perLender * 100) / 100, statement: s.fileName });
      });
    });
    return map;
  }, [statementTimeline]);

  const totalRevenue = statementTimeline.reduce((s, t) => s + t.credits, 0);

  return (
    <div className="space-y-4">
      {/* Upload / Send to OCR */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">Auto Underwrite</h3>
          {ocrStatus && <span className="text-xs text-green-600">{ocrStatus}</span>}
        </div>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 mb-1">Upload Bank Statement</label>
            <input type="file" accept=".pdf" id="ocr-file-input" className="w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100" />
          </div>
          <button onClick={async () => {
            const fileInput = document.getElementById('ocr-file-input');
            const file = fileInput?.files?.[0];
            if (!file) { setOcrStatus('Select a PDF first'); return; }
            setOcrSending(true); setOcrStatus('Sending to OCR...');
            try {
              const fd = new FormData(); fd.append('file', file); fd.append('clientId', id);
              const res = await fetch(`${API}/ocr/send`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
              const data = await res.json();
              if (!res.ok) throw new Error(data.error || 'Failed');
              setOcrStatus('Sent! Processing complete.');
              fetch(`${API}/ocr/results/${id}`, { headers }).then(r => r.ok ? r.json() : []).then(d => setOcrResults(d)).catch(() => {});
            } catch (e) { setOcrStatus(`Error: ${e.message}`); }
            setOcrSending(false);
          }} disabled={ocrSending} className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold text-xs rounded-xl flex items-center gap-2 whitespace-nowrap">
            {ocrSending ? 'Sending...' : 'Send to Underwriting'}
          </button>
        </div>
        {realDocs.filter(d => d.category === 'bank_statements').length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-400 mb-2">Or send an existing statement:</p>
            <div className="flex flex-wrap gap-2">
              {realDocs.filter(d => d.category === 'bank_statements').map(d => (
                <button key={d.id} onClick={async () => {
                  setOcrSending(true); setOcrStatus('Fetching & sending...');
                  try {
                    const res = await fetch(`${API}/ocr/send-to-go`, { method: 'POST', headers, body: JSON.stringify({ clientId: id, docId: d.id, bankType: 'manual' }) });
                    const data = await res.json(); if (!res.ok) throw new Error(data.error || 'Failed');
                    setOcrStatus('Sent!');
                  } catch (e) { setOcrStatus(`Error: ${e.message}`); }
                  setOcrSending(false);
                }} disabled={ocrSending} className="px-3 py-1.5 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-200 text-gray-700 text-xs rounded-lg transition-colors">
                  {d.fileName}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {docFinancials && (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-green-50/50 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-900">📊 Financial Summary</span>
            <span className="text-xs text-green-600 font-medium">{docFinancials.docsProcessed} statement{docFinancials.docsProcessed !== 1 ? 's' : ''} processed</span>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-green-50 rounded-xl p-3 text-center"><p className="text-xs text-green-600">Avg Monthly Revenue</p><p className="text-lg font-bold text-green-700">${Number(docFinancials.avgMonthlyRevenue||0).toLocaleString()}</p></div>
              <div className="bg-blue-50 rounded-xl p-3 text-center"><p className="text-xs text-blue-600">Est. Annual Revenue</p><p className="text-lg font-bold text-blue-700">${Number(docFinancials.estimatedAnnualRevenue||0).toLocaleString()}</p></div>
              <div className="bg-gray-50 rounded-xl p-3 text-center"><p className="text-xs text-gray-500">Total Credits</p><p className="text-lg font-bold text-gray-900">${Number(docFinancials.totalCredits||0).toLocaleString()}</p></div>
              <div className="bg-gray-50 rounded-xl p-3 text-center"><p className="text-xs text-gray-500"># Deposits</p><p className="text-lg font-bold text-gray-900">{docFinancials.numberOfDeposits||0}</p></div>
              <div className="bg-amber-50 rounded-xl p-3 text-center"><p className="text-xs text-amber-600">Lender Payments</p><p className="text-lg font-bold text-amber-700">${Number(docFinancials.totalLenderPayments||0).toLocaleString()}</p></div>
              <div className="bg-purple-50 rounded-xl p-3 text-center"><p className="text-xs text-purple-600">Withholding Rate</p><p className="text-lg font-bold text-purple-700">{docFinancials.withholdingRate||0}%</p></div>
              <div className="bg-red-50 rounded-xl p-3 text-center"><p className="text-xs text-red-600">NSF / Negative Days</p><p className="text-lg font-bold text-red-700">{docFinancials.negativeDays||0}</p></div>
              <div className="bg-indigo-50 rounded-xl p-3 text-center"><p className="text-xs text-indigo-600">Avg Daily Balance</p><p className="text-lg font-bold text-indigo-700">${Number(docFinancials.avgDailyBalance||0).toLocaleString()}</p></div>
            </div>
          </div>
        </div>
      )}

      {/* Lender Breakdown — Itemized */}
      {docFinancials && docFinancials.positions && docFinancials.positions.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">🏦 Lender Position Breakdown</span>
          </div>
          <div className="p-5 space-y-3">
            {docFinancials.positions.map((p, i) => {
              const pctOfRevenue = totalRevenue > 0 ? Math.round((p.totalPaid / totalRevenue) * 1000) / 10 : 0;
              const isExpanded = expandedLender === p.name;
              const monthlyData = lenderMonthly[p.name] || [];
              return (
                <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
                  <button onClick={() => setExpandedLender(isExpanded ? null : p.name)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-purple-700">{p.name}</span>
                      <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full">{p.occurrences} months active</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-bold text-amber-600">${Number(p.totalPaid||0).toLocaleString()}</span>
                      <span className="text-xs text-red-500 font-medium">{pctOfRevenue}% of revenue</span>
                      <ChevronRight size={14} className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </div>
                  </button>
                  {isExpanded && monthlyData.length > 0 && (
                    <div className="border-t border-gray-100 px-4 py-3 bg-gray-50">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-1.5 px-2 text-gray-400 font-medium">Period</th>
                            <th className="text-left py-1.5 px-2 text-gray-400 font-medium">Statement</th>
                            <th className="text-right py-1.5 px-2 text-gray-400 font-medium">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthlyData.map((m, j) => (
                            <tr key={j} className="border-b border-gray-100">
                              <td className="py-1.5 px-2 text-gray-700 font-mono">{m.period}</td>
                              <td className="py-1.5 px-2 text-gray-500 truncate max-w-[200px]">{m.statement}</td>
                              <td className="py-1.5 px-2 text-right font-bold text-amber-600">${Number(m.amount).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
            {/* Total lender % */}
            <div className="flex items-center justify-between px-4 py-2 bg-red-50 rounded-xl">
              <span className="text-xs font-semibold text-red-700">Total Lender Debits as % of Revenue</span>
              <span className="text-sm font-bold text-red-700">{totalRevenue > 0 ? Math.round(((docFinancials.totalLenderPayments||0) / totalRevenue) * 1000) / 10 : 0}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Statement-by-Statement Timeline Table */}
      {statementTimeline.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">📅 Statement Timeline</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">Period</th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">Credits</th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">Debits</th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">Cash Flow</th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">Lender Debits</th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">Avg Daily Bal</th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">NSFs</th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">Withholding</th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">Lenders</th>
                </tr>
              </thead>
              <tbody>
                {statementTimeline.map((s, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-blue-50/30">
                    <td className="py-2 px-3 font-semibold text-gray-800">{s.period}</td>
                    <td className="py-2 px-3 text-right text-green-600 font-bold">${Number(s.credits).toLocaleString()}</td>
                    <td className="py-2 px-3 text-right text-red-600 font-bold">${Number(s.debits).toLocaleString()}</td>
                    <td className={`py-2 px-3 text-right font-bold ${s.cashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>${Number(s.cashFlow).toLocaleString()}</td>
                    <td className="py-2 px-3 text-right text-amber-600 font-bold">${Number(s.lenderDebits).toLocaleString()}</td>
                    <td className="py-2 px-3 text-right text-indigo-600 font-bold">${Number(Math.round(s.avgDailyBalance)).toLocaleString()}</td>
                    <td className="py-2 px-3 text-right text-red-500 font-bold">{s.nsfCount}</td>
                    <td className="py-2 px-3 text-right text-purple-600">{Number(s.withholdingRate).toFixed(1)}%</td>
                    <td className="py-2 px-3">
                      <div className="flex flex-wrap gap-1">{s.lenders.map((l,j) => <span key={j} className="px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded text-[10px]">{l}</span>)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Charts */}
      {statementTimeline.length > 1 && (
        <div className="space-y-4">
          {/* Withholding Rate — MOVED UP for visibility */}
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5">
            <p className="text-sm font-semibold text-gray-900 mb-4">📊 Withholding Rate %</p>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={statementTimeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
                <Tooltip formatter={(v) => `${Number(v).toFixed(1)}%`} />
                <Line type="monotone" dataKey="withholdingRate" stroke="#9333ea" strokeWidth={2.5} name="Withholding %" dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Revenue & Debits */}
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5">
            <p className="text-sm font-semibold text-gray-900 mb-4">📈 Revenue vs Debits Over Time</p>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={statementTimeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="credits" stroke="#16a34a" strokeWidth={2} name="Credits (Revenue)" dot={{ r: 4 }} />
                <Line type="monotone" dataKey="debits" stroke="#dc2626" strokeWidth={2} name="Total Debits" dot={{ r: 4 }} />
                <Line type="monotone" dataKey="lenderDebits" stroke="#d97706" strokeWidth={2} name="Lender Debits" dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Cash Flow */}
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5">
            <p className="text-sm font-semibold text-gray-900 mb-4">💰 Cash Flow Over Time</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={statementTimeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                <Bar dataKey="cashFlow" name="Cash Flow" fill="#6366f1" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Avg Daily Balance */}
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5">
            <p className="text-sm font-semibold text-gray-900 mb-4">🏦 Average Daily Balance</p>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={statementTimeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => `$${Number(Math.round(v)).toLocaleString()}`} />
                <Line type="monotone" dataKey="avgDailyBalance" stroke="#4f46e5" strokeWidth={2.5} name="Avg Daily Balance" dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* NSF Count */}
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5">
            <p className="text-sm font-semibold text-gray-900 mb-4">⚠️ NSF Count</p>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={statementTimeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="nsfCount" name="NSFs" fill="#ef4444" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* No data state */}
      {!docFinancials && ocrResults.length === 0 && statementTimeline.length === 0 && (
        <p className="text-gray-400 text-sm">No underwriting data available. Upload bank statements to get started.</p>
      )}
    </div>
  );
}

function fmt(iso) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const TABS = ['Documents', 'Requests', 'Activity', 'Notes', 'Client Information', 'Seasonality', 'Underwriting', 'Auto Submit', 'Audit Trail'];

const SUPPORTED_BANKS = [
  { value: 'bofa', label: 'Bank of America' },
  { value: 'td', label: 'TD Bank' },
  { value: 'td_can', label: 'TD Canada' },
  { value: 'chase', label: 'Chase' },
  { value: 'rbc', label: 'RBC' },
  { value: 'regions', label: 'Regions' },
  { value: 'wellsfargo', label: 'Wells Fargo' },
  { value: 'truist', label: 'Truist' },
  { value: 'capitalone', label: 'Capital One' },
  { value: 'other', label: 'Other' },
];

// ─── SeasonalityTab Component ─────────────────────────────────────────────────
function SeasonalityTab({ statementTimeline, t }) {
  if (!statementTimeline || statementTimeline.length === 0) {
    return <p className="text-gray-400 text-sm">{t('No seasonality data available')}</p>;
  }

  const rows = statementTimeline.map(s => {
    const m = (s.period || '').match(/^([A-Za-z]+)\s*(\d{4})/);
    return {
      month: m ? m[1] : s.period,
      year: m ? m[2] : '',
      deposits: s.credits || 0,
      depositsCount: s.depositCount || Math.round((s.credits || 0) / 2500) || 0,
      ledger: s.avgDailyBalance || 0,
      nsf: s.nsfCount || 0,
    };
  });

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100">
        <span className="text-sm font-semibold text-gray-900">📅 {t('Seasonality')}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="text-left py-2.5 px-4 text-gray-500 font-medium text-xs">{t('Month')}</th>
              <th className="text-left py-2.5 px-4 text-gray-500 font-medium text-xs">{t('Year')}</th>
              <th className="text-right py-2.5 px-4 text-gray-500 font-medium text-xs">{t('Monthly Deposits')}</th>
              <th className="text-right py-2.5 px-4 text-gray-500 font-medium text-xs">{t('Deposits Per Month')}</th>
              <th className="text-right py-2.5 px-4 text-gray-500 font-medium text-xs">{t('Ledger ($)')}</th>
              <th className="text-right py-2.5 px-4 text-gray-500 font-medium text-xs">{t('NSF')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-gray-50 hover:bg-blue-50/30">
                <td className="py-2.5 px-4 font-semibold text-gray-800">{r.month}</td>
                <td className="py-2.5 px-4 text-gray-600">{r.year}</td>
                <td className="py-2.5 px-4 text-right text-green-600 font-bold">${Number(r.deposits).toLocaleString()}</td>
                <td className="py-2.5 px-4 text-right text-gray-700 font-medium">{r.depositsCount}</td>
                <td className="py-2.5 px-4 text-right text-indigo-600 font-bold">${Number(Math.round(r.ledger)).toLocaleString()}</td>
                <td className={`py-2.5 px-4 text-right font-bold ${r.nsf > 0 ? 'text-red-600' : 'text-gray-400'}`}>{r.nsf}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── ClientInformationTab ──────────────────────────────────────────────────
// Holds every field needed to submit this deal to each lender's API — mirrors
// the Zoho "Application" layout, including the per-lender entity type fields
// (Kapitus/Idea/CAN/Channel Partners/Forward/Quantum/Britecap each use their
// own enum) and application-tracking IDs written back after submission.
const KAPITUS_ENTITY_TYPES = [
  'Limited Liability Company (LLC)', 'S Corporation (S Corp)', 'C Corporation (C Corp)',
  'Limited Liability Partnership (LLP)', 'Sole Proprietorship', 'Professional Limited Liability Company (PLLC)',
  'Professional Corporation (PC)', 'Limited Partnership (LP)', 'Limited Liability Limited Partnership (LLLP)',
  'General Partnership (GP)',
];
const IDEA_ENTITY_TYPES = [
  'other', 'sole-proprietorship', 'limited-liability-company', 'corporation', 'limited-partnership',
  'general-partnership', 'not-for-profit', 'public', 'legal-partnership',
];
const CAN_ENTITY_TYPES = ['Other', 'Corporation', 'Partnership', 'LLC', 'LLP', 'Sole Proprietorship', 'Limited Partnership'];
const CHANNEL_PARTNERS_ENTITY_TYPES = ['C Corp', 'LLC', 'S Corp', 'Partnership', 'Non Profit', 'Corporation', 'LLP', 'Sole Proprietorship', 'Limited Partnership', 'Other'];
const FORWARD_ENTITY_TYPES = ['Sole Proprietor', 'Limited Liability Company (LLC)', 'Corporation', 'Limited Partnership (LP)', 'Limited Liability Partnership (LLP)', 'General Partnership'];
const QUANTUM_ENTITY_TYPES = ['LLC', 'Sole Proprietor', 'Partnership', 'S Corporation', 'C Corporation', 'Other'];
const BRITECAP_ENTITY_TYPES = [
  'Sole Proprietorship', 'Corporation', 'Limited Liability Company', 'Professional Association',
  'General Partnership', 'Limited Partnership', 'Non-Profit', 'Professional Corporation', 'Limited Liability Partnership',
];
// Confirmed Zoho picklist (Peac_Entity_Type, per Automated Submission Email/
// app/peac_index_additions.js's header comment, dated 2026-09) - exact
// wording/casing, only 4 values, coarser than every other lender's entity
// enum here.
const PEAC_ENTITY_TYPES = ['Sole proprietorship', 'Partnership', 'Corporation', 'Limited Liability Company (LLC)'];

const entityOptions = (list) => [{ value: '', label: '-None-' }, ...list.map(v => ({ value: v, label: v }))];

// Mirrors the "JK - Entity Sync" Zoho CRM automation (fires on Entity_Type1
// change, derives every other lender's entity type from Kapitus's value).
// Replicated here so the same cascade happens instantly in the Client
// Information tab without waiting on a Zoho round-trip.
// peac: derived the same way as every other column here, collapsed onto
// PEAC's own much coarser 4-value enum (PEAC_ENTITY_TYPES above) - not
// confirmed against a live "JK - Entity Sync" Zoho automation update the way
// the other columns are, since Peac_Entity_Type is a brand-new field (see
// PEAC_ENTITY_TYPES's own comment), but every Kapitus value maps onto exactly
// one of PEAC's 4 categories with no ambiguity either way.
const KAPITUS_ENTITY_MAP = {
  'Limited Liability Company (LLC)':             { idea: 'limited-liability-company', can: 'LLC',                 channel: 'LLC',                 forward: 'Limited Liability Company (LLC)',   quantum: 'LLC',            britecap: 'Limited Liability Company', peac: 'Limited Liability Company (LLC)' },
  'Professional Limited Liability Company (PLLC)': { idea: 'limited-liability-company', can: 'LLC',                 channel: 'LLC',                 forward: 'Limited Liability Company (LLC)',   quantum: 'LLC',            britecap: 'Limited Liability Company', peac: 'Limited Liability Company (LLC)' },
  // channel is S Corp / C Corp, not 'Corporation': Channel Partners has its
  // own distinct S Corp and C Corp options (CHANNEL_PARTNERS_ENTITY_TYPES),
  // and mapCpBusinessType server-side matches 'Corporation' on neither -
  // it fell through to its LLC default, so every corporation went to
  // Channel Partners as an LLC. PC follows Quantum's column (a C corp).
  'S Corporation (S Corp)':                      { idea: 'corporation',               can: 'Corporation',          channel: 'S Corp',               forward: 'Corporation',                       quantum: 'S Corporation',  britecap: 'Corporation', peac: 'Corporation' },
  'C Corporation (C Corp)':                      { idea: 'corporation',               can: 'Corporation',          channel: 'C Corp',               forward: 'Corporation',                       quantum: 'C Corporation',  britecap: 'Corporation', peac: 'Corporation' },
  'Professional Corporation (PC)':                { idea: 'corporation',               can: 'Corporation',          channel: 'C Corp',               forward: 'Corporation',                       quantum: 'C Corporation',  britecap: 'Corporation', peac: 'Corporation' },
  'Limited Liability Partnership (LLP)':          { idea: 'limited-partnership',       can: 'LLP',                  channel: 'LLP',                  forward: 'Limited Liability Partnership (LLP)', quantum: 'Partnership',   britecap: 'Limited Liability Partnership', peac: 'Partnership' },
  'Limited Liability Limited Partnership (LLLP)': { idea: 'limited-partnership',       can: 'LLP',                  channel: 'LLP',                  forward: 'Limited Liability Partnership (LLP)', quantum: 'Partnership',   britecap: 'Limited Liability Partnership', peac: 'Partnership' },
  'Sole Proprietorship':                         { idea: 'sole-proprietorship',       can: 'Sole Proprietorship',  channel: 'Sole Proprietorship',  forward: 'Sole Proprietor',                    quantum: 'Sole Proprietor', britecap: 'Sole Proprietorship', peac: 'Sole proprietorship' },
  'Limited Partnership (LP)':                     { idea: 'limited-partnership',       can: 'Limited Partnership',  channel: 'Limited Partnership',  forward: 'Limited Partnership (LP)',           quantum: 'Partnership',    britecap: 'Limited Partnership', peac: 'Partnership' },
  'General Partnership (GP)':                     { idea: 'general-partnership',       can: 'Other',                channel: 'Other',                forward: 'General Partnership',                quantum: 'Other',          britecap: 'General Partnership', peac: 'Partnership' },
};

// Ownership % follows the owner count: 100 with one owner, 50/50 once a
// secondary owner is named - always, over whatever was there. The same rule
// the server applies to the whole record on every save (applyOwnershipSplit
// in clientStore.js), run here too so the tab shows what the server stores
// instead of a number that silently changes underneath the rep.
function withDerivedOwnership(f) {
  if (!String(f.ownerName || '').trim()) return f;
  return String(f.secondaryOwnerName || '').trim()
    ? { ...f, ownershipPercent: '50', secondaryOwnershipPercent: '50' }
    // One owner: 100, and no second owner's share left behind.
    : { ...f, ownershipPercent: '100', secondaryOwnershipPercent: '' };
}

// What to show in Business Description before a rep has typed one: the
// merchant's own words from the signed application, which arrive as the
// envelope's "business sic" tab (3,133 of 4,839 records have it), then the
// industry the deal was filed under - skipping the placeholder values that
// tell a rep nothing.
//
// Display only. The apps are rebuilt from the saved field alone (see
// businessDescriptionOf in server/src/services/appVariants.js), so a deal
// nobody has edited keeps whatever its application already prints.
const DESCRIPTION_TABS = ['business sic', 'business_sic', 'business description',
                          'business disc', 'businesssic'];
const PLACEHOLDER_INDUSTRY = /^(other|n\/?a|none|unknown)$/i;
const resolveBusinessDescription = (client) => {
  const saved = String(client?.businessDescription || '').trim();
  if (saved) return saved;
  const tabs = client?.tabData;
  if (tabs && typeof tabs === 'object') {
    for (const [k, v] of Object.entries(tabs)) {
      if (DESCRIPTION_TABS.includes(String(k).toLowerCase().trim()) && String(v || '').trim()) {
        return String(v).trim();
      }
    }
  }
  const industry = String(client?.industry || '').trim();
  return industry && !PLACEHOLDER_INDUSTRY.test(industry) ? industry : '';
};

const CLIENT_INFO_SECTIONS = [
  {
    title: 'Business',
    fields: [
      { key: 'businessName',      label: 'Business Legal Name' },
      { key: 'dba',                label: 'Doing Business As (DBA)' },
      // optional: the Business section is one of the three that drive the
      // "incomplete info" count and the red asterisks. Every deal would
      // otherwise show as missing a field the moment this one was added, for
      // a description no lender requires.
      { key: 'businessDescription', label: 'Business Description', optional: true },
      { key: 'address',            label: 'Business Address' },
      { key: 'businessCity',      label: 'Business City' },
      { key: 'state',              label: 'Business State' },
      { key: 'zip',                label: 'Business Zip' },
      { key: 'phone',              label: 'Business Phone' },
      { key: 'businessEmail',    label: 'Business Email' },
      { key: 'businessStartDate', label: 'Business Start Dates' },
      { key: 'ein',                label: 'Federal Tax ID' },
    ],
  },
  {
    title: 'Principal Owner',
    fields: [
      { key: 'ownerName',         label: 'Principle Owner Name' },
      { key: 'ownershipPercent', label: 'Principle Ownership %' },
      { key: 'ownerPhone',       label: 'Principle Phone' },
      { key: 'email',             label: 'Principle Email' },
      { key: 'ownerAddress',     label: 'Principle Address' },
      { key: 'ownerCity',        label: 'Principle City' },
      { key: 'ownerState',       label: 'Principle State' },
      { key: 'ownerZip',         label: 'Principle Zip' },
      { key: 'ownerSSN',         label: 'Principle SSN' },
      { key: 'ownerDOB',         label: 'Principle DOB' },
    ],
  },
  {
    title: 'Secondary Owner',
    fields: [
      { key: 'secondaryOwnerName',      label: 'Secondary Owner Name' },
      { key: 'secondaryOwnershipPercent', label: 'Secondary Ownership %' },
      { key: 'secondaryPhone',          label: 'Secondary Phone' },
      { key: 'secondaryEmail',          label: 'Secondary Email' },
      { key: 'secondaryAddress',        label: 'Secondary Address' },
      { key: 'secondaryCity',           label: 'Secondary City' },
      { key: 'secondaryState',          label: 'Secondary State' },
      { key: 'secondaryZip',            label: 'Secondary Zip' },
      { key: 'secondarySSN',            label: 'Secondary SSN' },
      { key: 'secondaryDOB',            label: 'Secondary DOB' },
    ],
  },
  {
    title: 'Portal & Deal',
    fields: [
      { key: 'portalMonthlyRev', label: 'Portal Monthly Rev', prefix: '$' },
      { key: 'portalMobile',     label: 'Portal Mobile' },
      { key: 'portalYearlyRev',  label: 'Portal Yearly Rev', prefix: '$' },
      { key: 'portalEmail',      label: 'Portal Email' },
      { key: 'naicsCode',        label: 'NAICS Code' },
      // Was a disabled select with ONE option, 'over-two-years', so nobody
      // could correct it for any client - and that string reached the lender
      // matcher, where Number('over-two-years') is NaN and the whole TIB gate
      // was skipped. Now a number of years, which is what the matcher and every
      // lender's minTIB actually compare against. Derived from Business Start
      // Date when that is set, so the common case needs no typing and the two
      // cannot drift apart.
      { key: 'timeInBusiness',   label: 'Time in Business (years)', type: 'text', derivedFrom: 'businessStartDate' },
      // Computed from Portal Monthly Rev, not typed and not pulled - so it is
      // read-only. An editable field whose value is overwritten on every save
      // is a trap: the number a rep types disappears on the next save with no
      // sign it was ever discarded.
      { key: 'requestedAmount',  label: 'Requested Funding Amount', computedFrom: 'portalMonthlyRev', computedNote: 'Portal Monthly Rev + $5,000' },
      { key: 'intendedUseOfFunds', label: 'Intended Use of Funds', type: 'select', options: [{ value: 'Other Business Expenses', label: 'Other Business Expenses' }], fixed: true },
      { key: 'useOfFunds',         label: 'Use of Funds',           type: 'select', options: [{ value: 'Business Expansion', label: 'Business Expansion' }], fixed: true },
      { key: 'industry',           label: 'Industry App',           type: 'select', options: [{ value: 'Other', label: 'Other' }], fixed: true },
      // Confirmed real Zoho picklist (Peac_Purpose_of_Loan, per Automated
      // Submission Email/app/peac_index_additions.js) - same fixed-select
      // treatment as intendedUseOfFunds/useOfFunds/industry just above,
      // for the same reason: PEAC's full accepted list of values isn't
      // documented anywhere this repo has access to, so every deal locks to
      // the one value known safe. 'Expansion' rather than "Cash Crunch"
      // (the one PEAC-confirmed value, from testpeac.cjs) per instruction -
      // Capital Infusion's own chosen default, not a PEAC confirmation.
      { key: 'peacPurposeOfLoan', label: 'Peac Purpose of Loan', type: 'select', options: [{ value: 'Expansion', label: 'Expansion' }], fixed: true },
      // Drives Orbit's own renewal rules and nothing else - it is never sent
      // to a lender. On 'Renewal' the deal name keeps its "- 2" / "- 3"
      // (Link Data and edits cannot strip it), while the Business Legal Name
      // and DBA show - and reach lenders - without it. Set automatically on a
      // renewal made by the Renewal button or by Zoho; set by hand to make an
      // existing deal a renewal. See isRenewalClient in services/lenderName.js.
      { key: 'orbitDealType', label: 'Deal Type', type: 'select', options: [{ value: 'New', label: 'New' }, { value: 'Renewal', label: 'Renewal' }] },
    ],
  },
  {
    title: 'Lender Entity Types',
    note: 'Setting Kapitus Entity Type fills in every other lender\'s entity type below. Change one afterward only if that lender needs something different.',
    fields: [
      { key: 'kapitusEntityType',         label: 'Kapitus Entity Type',         type: 'select', options: entityOptions(KAPITUS_ENTITY_TYPES) },
      { key: 'ideaEntityType',            label: 'Idea Entity Type',            type: 'select', options: entityOptions(IDEA_ENTITY_TYPES) },
      { key: 'canEntityType',             label: 'CAN Entity Type',             type: 'select', options: entityOptions(CAN_ENTITY_TYPES) },
      { key: 'channelPartnersEntityType', label: 'Channel Partners Entity',     type: 'select', options: entityOptions(CHANNEL_PARTNERS_ENTITY_TYPES) },
      { key: 'forwardEntityType',         label: 'Forward Entity Type',         type: 'select', options: entityOptions(FORWARD_ENTITY_TYPES) },
      { key: 'quantumEntityType',         label: 'Quantum Entity Type',         type: 'select', options: entityOptions(QUANTUM_ENTITY_TYPES) },
      { key: 'britecapEntityType',        label: 'Britecap Entity Type',        type: 'select', options: entityOptions(BRITECAP_ENTITY_TYPES) },
      { key: 'peacEntityType',            label: 'Peac Entity Type',            type: 'select', options: entityOptions(PEAC_ENTITY_TYPES) },
    ],
  },
  {
    title: 'Application IDs',
    fields: [
      // The deal's own Zoho Accounts record id, captured automatically by
      // Auto Submit's Link Data (see MAP.zohoUniqueId there) whenever the
      // record it resolved lives in Accounts. Shown here - rather than kept
      // invisible the way zohoId always has been - so a rep can see at a
      // glance whether a deal actually has one, and paste one in by hand
      // when Link Data couldn't find it (a deal still sitting in Leads or
      // Deals, say). This is what submitting to lenders reads to find the
      // Account to append the Lenders subform row to and update
      // Stage_of_Package on.
      { key: 'zohoUniqueId',          label: 'Zoho Unique ID' },
      { key: 'kapitusUUID',           label: 'applicationId Kapitus' },
      { key: 'applicationIdIdea',     label: 'applicationId Idea' },
      { key: 'applicationIdCAN',      label: 'applicationId CAN' },
      { key: 'applicationIdOnDeck',   label: 'applicationId OnDeck' },
      // Split US/Canada - server-side (APPLICATION_ID_ORBIT_FIELD in
      // server/src/routes/autoSubmit.js) already saves specialty and
      // specialtyca to these two distinct keys; this panel just wasn't
      // showing the Canada one, so that value was landing on the client
      // record with nowhere for a rep to see it.
      { key: 'applicationIdSpecialty',       label: 'applicationId Specialty (US)' },
      { key: 'applicationIdSpecialtyCanada', label: 'applicationId Specialty (Canada)' },
      { key: 'applicationIdForward',  label: 'applicationId Forward' },
      { key: 'applicationIdChannel',  label: 'applicationId Channel' },
      { key: 'applicationIdCredibly', label: 'applicationId Credibly' },
      { key: 'applicationIdBitty',    label: 'applicationId Bitty' },
      { key: 'applicationIdQuantum',  label: 'applicationId Quantum' },
      { key: 'applicationIdBritecap', label: 'applicationId Britecap' },
      { key: 'applicationIdPeac',     label: 'applicationId PEAC' },
      { key: 'applicationIdRadiance', label: 'applicationId Radiance' },
    ],
  },
];

// Same Canada/US question AutoSubmit's own normaliseState() answers for
// lender routing (see its "Geography" section) - kept as a lightweight
// local copy rather than a static import from AutoSubmit.jsx, since that
// module is lazy-loaded on this page (see the `lazy(() => import('./AutoSubmit'))`
// above) and a static import of it here would pull its whole module in
// eagerly, defeating the point of lazy-loading it.
// Only needs to answer "does this look Canadian", not resolve an exact
// province code, so it's far shorter than the full normaliseState.
const CANADA_PROVINCE_CODES = new Set(['AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QB','QC','SK','YT']);
const CANADA_PROVINCE_NAMES_RE = /ALBERTA|BRITISH COLUMBIA|MANITOBA|NEW BRUNSWICK|NEWFOUNDLAND|NOVA SCOTIA|NORTHWEST TERRITORIES|NUNAVUT|ONTARIO|PRINCE EDWARD ISLAND|QUEBEC|SASKATCHEWAN|YUKON/;
function looksCanadian(raw) {
  const s = String(raw ?? '').trim().toUpperCase().replace(/\./g, '');
  if (!s) return false;
  // 'CAN' is Auto Submit's State dropdown value for "CANADA province not
  // specified" - real, and Canadian, but no province code.
  if (s === 'CAN' || CANADA_PROVINCE_CODES.has(s)) return true;
  if (/\bCANADA\b|\bCANADIAN\b/.test(s)) return true;
  return CANADA_PROVINCE_NAMES_RE.test(s);
}

// A renewal's deal is named "<business> - 2" so it can be told apart from the
// original, but the business itself is still "<business>". Orbit stores the
// deal name (number included) in client.businessName, and Client Information
// shows the Business Legal Name and DBA WITHOUT the number: the tab strips it
// on the way in and puts it back on the way out, so editing the legal name
// never renames the deal. Mirrors services/lenderName.js on the server, which
// enforces the same thing for every write.
// Deal Type decides when it is set; a record from before the field existed
// falls back to how it was created. Same rule as the server's.
const isRenewalDeal = (client) => {
  if (!client) return false;
  const type = String(client.orbitDealType || '').trim().toLowerCase();
  if (type === 'renewal') return true;
  if (type === 'new') return false;
  return !!(client.renewalOf || client.source === 'Zoho renewal');
};
const RENEWAL_NUMBER_RE = /\s*-\s*(\d+)$/;
function renewalNumberOf(client) {
  if (!isRenewalDeal(client)) return '';
  const m = String(client?.businessName ?? '').trim().match(RENEWAL_NUMBER_RE);
  return m ? m[1] : '';
}
const withoutRenewalNumber = (name) => String(name ?? '').replace(RENEWAL_NUMBER_RE, '').trim();
// Form values -> what is stored: the deal name gets its number back.
// The number is never typed here: it belongs to the deal name, which a super
// admin edits by double-clicking the page heading. Anything like "- 3" typed
// into Business Legal Name on a renewal is dropped.
function withRenewalDealName(values, client) {
  const name = String(values?.businessName ?? '').trim();
  if (!isRenewalDeal(client) || !name || !('businessName' in (values || {}))) return values;
  const n = renewalNumberOf(client);
  const legal = withoutRenewalNumber(name) || name;
  return { ...values, businessName: n ? `${legal} - ${n}` : legal };
}

// DBA follows the Business Legal Name: whenever the business name is set, the
// DBA becomes the same value, overwriting whatever it held. A blank business
// name leaves the DBA alone. Returns the DBA to set, or null for no change.
function dbaFollowingBusinessName(businessName, currentDba) {
  const name = String(businessName ?? '').trim();
  if (!name || String(currentDba ?? '') === name) return null;
  return name;
}

// Business Start Date and Principal/Secondary DOB arrive in whatever format
// their source used - MM/DD/YYYY (what businessStartDate gets normalized to
// server-side), MM/YYYY, ISO, or a DocuSign tab's own free-text date - so this
// tries the formats actually seen in this codebase before falling back to the
// Date constructor. Returns { month, year } (month 1-indexed) or null for
// anything it can't parse - an unparseable date is a different problem from
// an impossible one, and isn't what this specific check is for.
function parseMonthYear(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); // MM/DD/YYYY
  if (m) return { month: Number(m[1]), year: Number(m[3]) };
  m = s.match(/^(\d{1,2})\/(\d{4})$/); // MM/YYYY
  if (m) return { month: Number(m[1]), year: Number(m[2]) };
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/); // ISO
  if (m) return { month: Number(m[2]), year: Number(m[1]) };
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return { month: d.getMonth() + 1, year: d.getFullYear() };
  return null;
}

// True when a date falls in the current calendar month and year - the one
// thing both Business Start Date and Principal/Secondary DOB can never
// legitimately be. A business that "started" this month, or an owner "born"
// this month, is a placeholder or typo (today's date pasted in by mistake,
// a form default that was never overwritten), not a real answer, and every
// lender that receives it treats it as a real one.
function isCurrentMonthYear(raw) {
  const parsed = parseMonthYear(raw);
  if (!parsed) return false;
  const now = new Date();
  return parsed.month === now.getMonth() + 1 && parsed.year === now.getFullYear();
}

function ClientInformationTab({ client, id, headers, onSaved, docFinancials, onAppsRecreated }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  // 'idle' | 'saving' | 'saved' | 'error' - drives the small status pill next
  // to Save so an autosave (blur or the typing-pause debounce) is visible
  // instead of silent, the same way a manual Save click already was.
  const [saveStatus, setSaveStatus] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);

  useEffect(() => {
    const next = {};
    CLIENT_INFO_SECTIONS.forEach(section => section.fields.forEach(f => {
      next[f.key] = f.fixed ? f.options[0].value : (client?.[f.key] ?? '');
    }));
    // Nothing is written back by this - saving the form is what persists the
    // field, exactly like any other.
    if (!String(next.businessDescription || '').trim()) {
      next.businessDescription = resolveBusinessDescription(client) || '';
    }
    // Requested Funding Amount is pre-filled, never pulled: Portal Monthly Rev
    // + 5,000, recomputed on every load so what the tab shows is what the
    // server will store on the next save. Once a rep types their own figure
    // (requestedAmountManual) it is theirs - the server keeps it too, so the
    // two still agree. Left alone when there is no monthly figure to compute
    // from, since blanking it would send a 0 rather than the last known request.
    // Deal Type has never been saved on older records - show what applies.
    if (!String(next.orbitDealType || '').trim()) next.orbitDealType = isRenewalDeal(client) ? 'Renewal' : 'New';
    // A renewal: Business Legal Name and DBA are shown without the "- N".
    if (renewalNumberOf(client)) {
      next.businessName = withoutRenewalNumber(next.businessName);
      if (next.dba) next.dba = withoutRenewalNumber(next.dba) || next.dba;
    }
    next.requestedAmountManual = client?.requestedAmountManual ?? '';
    if (next.requestedAmountManual !== true) {
      const monthly = parseFloat(String(next.portalMonthlyRev || '').replace(/[^0-9.]/g, '')) || 0;
      if (monthly > 0) next.requestedAmount = String(Math.round(monthly + 5000));
    }
    // setField cleans these on the way in, but plenty of records were written
    // before it did - and a record that arrives holding "$75,000" or a
    // hyphenated SSN is exactly the one that silently breaks a lender mapper
    // or a credit pull, because nobody has to retype a field that already
    // looks right. Cleaning on load means opening the tab is enough to fix it,
    // and the next save persists it.
    if (String(next.requestedAmount || '').trim()) {
      const s = String(next.requestedAmount).replace(/[^0-9.]/g, '');
      const [whole, ...rest] = s.split('.');
      next.requestedAmount = rest.length ? `${whole}.${rest.join('')}` : whole;
    }
    ['ownerSSN', 'secondarySSN', 'ein'].forEach(k => {
      if (String(next[k] || '').trim()) next[k] = String(next[k]).replace(/\D/g, '').slice(0, 9);
    });
    // Channel Partners saved as a bare 'Corporation' next to a Kapitus S/C
    // corp is the old cascade's output - it wrote 'Corporation' for every
    // corporation, which goes to Channel Partners as an LLC. The cascade only
    // re-runs when Kapitus is changed, so records saved before the fix kept
    // it. Re-derived from Kapitus here; same fix-on-load as the cleaning above.
    const derivedChannel = KAPITUS_ENTITY_MAP[next.kapitusEntityType]?.channel;
    if (next.channelPartnersEntityType === 'Corporation' && derivedChannel && derivedChannel !== 'Corporation') {
      next.channelPartnersEntityType = derivedChannel;
    }
    setForm(withDerivedOwnership(next));
  }, [client]);

  const autoSaveTimer = React.useRef(null);
  // Refs, not the state values directly, because flushChanges is called from
  // timers/blur/unmount handlers that closed over whatever form/client were
  // at the moment they were scheduled - stale by the time they actually run
  // otherwise, which is exactly how the old per-field timer lost edits: a
  // second field's edit reset the single shared timer and cancelled the
  // first field's still-unsent save outright.
  const formRef = React.useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);
  const clientRef = React.useRef(client);
  useEffect(() => { clientRef.current = client; }, [client]);
  // headers and onSaved are both recreated fresh by the parent on every
  // render (getHeaders() and an inline arrow function, neither memoized) -
  // closing over them directly would give flushChanges a new identity on
  // nearly every parent re-render, which would re-fire the unmount-flush
  // effect below far more often than an actual unmount, on totally
  // unrelated state changes elsewhere on the page. Refs sidestep that:
  // flushChanges only ever depends on `id`.
  const headersRef = React.useRef(headers);
  useEffect(() => { headersRef.current = headers; }, [headers]);
  const onSavedRef = React.useRef(onSaved);
  useEffect(() => { onSavedRef.current = onSaved; }, [onSaved]);
  const isFlushingRef = React.useRef(false);
  const needsFlushRef = React.useRef(false);
  // Whether the most recent save attempt succeeded (null until one has run).
  const lastFlushOkRef = React.useRef(null);
  // Same reason as onSavedRef above - the parent passes a fresh function each render.
  const onAppsRecreatedRef = React.useRef(onAppsRecreated);
  useEffect(() => { onAppsRecreatedRef.current = onAppsRecreated; }, [onAppsRecreated]);

  // The one save path. Diffs the live form against the last known server
  // state - not a per-field "what just changed" object - so it always sends
  // everything edited since the last successful save, no matter how many
  // fields were touched or how quickly. silent:true (blur, debounce, unmount)
  // updates the small status pill only; silent:false (the Save button) also
  // drives the button's own disabled/label state and the "Nothing to save"/
  // "Failed" message.
  // Returns true when everything is saved (or there was nothing to save) and
  // false when the save failed - Recreate Apps waits on this so it never
  // regenerates from information that did not reach the record. Other callers
  // ignore the result.
  const flushChanges = React.useCallback(async ({ silent = true } = {}) => {
    if (isFlushingRef.current) { needsFlushRef.current = true; return; }
    const changed = {};
    // withRenewalDealName: on a renewal the box holds the legal name without
    // its "- N"; what is compared, saved and handed back is the deal name.
    const stored = withRenewalDealName(formRef.current, clientRef.current);
    Object.entries(stored).forEach(([k, v]) => {
      if (v !== (clientRef.current?.[k] ?? '')) changed[k] = v;
    });
    if (!Object.keys(changed).length) {
      if (!silent) { setSaveMsg('Nothing to save'); setTimeout(() => setSaveMsg(''), 4000); }
      lastFlushOkRef.current = true;
      return true;
    }
    isFlushingRef.current = true;
    setSaveStatus('saving');
    if (!silent) setSaving(true);
    try {
      const res = await fetch(`${API}/clients-api/${id}`, {
        method: 'PATCH', headers: headersRef.current,
        body: JSON.stringify({ ...changed, _editSource: 'manual' }),
      });
      if (!res.ok) throw new Error('Save failed');
      onSavedRef.current(stored);
      setSaveStatus('saved');
      setLastSavedAt(Date.now());
      if (!silent) setSaveMsg('✅ Saved');
      lastFlushOkRef.current = true;
      return true;
    } catch (e) {
      setSaveStatus('error');
      if (!silent) setSaveMsg(`Failed to save: ${e.message}`);
      lastFlushOkRef.current = false;
      return false;
    } finally {
      isFlushingRef.current = false;
      if (!silent) { setSaving(false); setTimeout(() => setSaveMsg(''), 4000); }
      // A field was edited while this save was in flight - that edit is
      // sitting in formRef but was never included, so send it now rather
      // than waiting for the next blur or debounce tick.
      if (needsFlushRef.current) { needsFlushRef.current = false; flushChanges({ silent: true }); }
    }
  }, [id]);

  // Catches anything still unsent if the rep switches tabs (this component
  // unmounts - see the `activeTab === 'Client Information' &&` guard around
  // it) or navigates away before the debounce/blur flush already in flight
  // finishes on its own.
  useEffect(() => {
    return () => { flushChanges({ silent: true }); };
  }, [flushChanges]);

  const setField = (key, value) => {
    setForm(prev => {
      // Punctuation in a number field is noise, not a typo, and it is worth
      // silently fixing rather than just flagging red - a rep pasting
      // "$48,813.00" from somewhere typed the right figure, and the lender
      // mappers can't parse what's around it (see amountProblem's own comment
      // on why a stray comma is dangerous rather than merely rejected).
      // Everything that isn't a digit or a decimal point comes out, and only
      // the first decimal point survives, so "1.2.3" doesn't become the
      // unparseable "1.23". Letters land on the floor with the rest: there is
      // no amount they could have been part of.
      const cleanAmount = (v) => {
        const s = String(v).replace(/[^0-9.]/g, '');
        const [whole, ...rest] = s.split('.');
        return rest.length ? `${whole}.${rest.join('')}` : whole;
      };
      // Same treatment for the ID fields, for the same reason: "123-45-6789"
      // is how a human writes an SSN and how Experian refuses one. Stripping
      // it here is what lets the credit pull fire off the record at all.
      //
      // Ownership % gets cleanAmount's digits+decimal treatment too, same
      // reason as Requested Funding Amount - every lender mapper does
      // Number(client.ownershipPercent), and "100%" (typed with the sign,
      // exactly as the field prints it) silently becomes NaN there.
      const cleaned =
        key === 'requestedAmount' ? cleanAmount(value)
        : (key === 'ownerSSN' || key === 'secondarySSN' || key === 'ein')
          ? String(value).replace(/\D/g, '').slice(0, 9)
        : (key === 'ownershipPercent' || key === 'secondaryOwnershipPercent')
          ? cleanAmount(value)
        : value;
      const updates = { [key]: cleaned };
      // A rep typing a Requested Funding Amount makes it theirs: the formula
      // below and the server both leave it alone from then on. Clearing the
      // field hands it back to the formula.
      if (key === 'requestedAmount') updates.requestedAmountManual = String(cleaned).trim() !== '';
      // When Portal Monthly Rev changes, Requested Amount is recomputed as
      // portalMonthlyRev + 5,000 - unless a rep has typed their own amount,
      // which a revenue change must not overwrite.
      // Business Start Date is the fact; Time in Business is derived from it.
      // Keeping them independent is what produced 144 records labelled
      // over-two-years whose own start date said under two. Typing over the
      // derived value is still allowed - some records have a date that is
      // wrong and a TIB that is right - it just stops being the default.
      if (key === 'businessStartDate') {
        const y = yearsSince(value);
        if (y != null) updates.timeInBusiness = String(y);
      }
      if (key === 'portalMonthlyRev' && prev.requestedAmountManual !== true) {
        const monthly = parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0;
        if (monthly > 0) updates.requestedAmount = String(Math.round(monthly + 5000));
      }
      // Changing Kapitus Entity Type cascades to every other lender's entity
      // type, mirroring the "JK - Entity Sync" Zoho automation. Clearing it
      // back to -None- (value '') cascades a clear to every other lender too,
      // rather than leaving them on a now-stale entity type.
      if (key === 'kapitusEntityType') {
        if (KAPITUS_ENTITY_MAP[value]) {
          const m = KAPITUS_ENTITY_MAP[value];
          updates.ideaEntityType            = m.idea;
          updates.canEntityType             = m.can;
          updates.channelPartnersEntityType = m.channel;
          updates.forwardEntityType         = m.forward;
          updates.quantumEntityType         = m.quantum;
          updates.britecapEntityType        = m.britecap;
          updates.peacEntityType            = m.peac;
        } else if (value === '') {
          updates.ideaEntityType            = '';
          updates.canEntityType             = '';
          updates.channelPartnersEntityType = '';
          updates.forwardEntityType         = '';
          updates.quantumEntityType         = '';
          updates.britecapEntityType        = '';
          updates.peacEntityType            = '';
        }
      }
      // Adding or removing the secondary owner flips the split right away,
      // and the new percentages go out with the save as changed fields.
      const next = withDerivedOwnership({ ...prev, ...updates });
      // Debounced flush - covers a rep still actively typing in one field,
      // so it doesn't fire a save on every keystroke. onBlur (below) is what
      // actually catches "moved to the next field" promptly; this is a
      // fallback for a field nobody ever blurs (the very last one edited
      // before the rep does something else entirely, e.g. clicks Save or a
      // tab).
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = setTimeout(() => flushChanges({ silent: true }), 1500);
      return next;
    });
  };

  const handleBlur = () => {
    if (autoSaveTimer.current) { clearTimeout(autoSaveTimer.current); autoSaveTimer.current = null; }
    flushChanges({ silent: true });
  };

  // Hands a rep-edited computed field (Requested Funding Amount) back to its
  // formula and saves right away. With no monthly figure to compute from, the
  // amount is left as-is and only stops being treated as the rep's own.
  const resetToFormula = (f) => {
    const current = formRef.current;
    const monthly = parseFloat(String(current[f.computedFrom] || '').replace(/[^0-9.]/g, '')) || 0;
    const next = { ...current, requestedAmountManual: false, ...(monthly > 0 ? { [f.key]: String(Math.round(monthly + 5000)) } : {}) };
    formRef.current = next;
    setForm(next);
    handleBlur();
  };

  // Leaving Business Legal Name copies it into the DBA beside it, the moment
  // the box loses focus. formRef is updated directly as well as state, so the
  // save handleBlur starts right away includes the DBA in the same request.
  const handleBusinessNameBlur = () => {
    const current = formRef.current;
    const nextDba = dbaFollowingBusinessName(current.businessName, current.dba);
    if (nextDba !== null) {
      const next = { ...current, dba: nextDba };
      formRef.current = next;
      setForm(next);
    }
    handleBlur();
  };

  const handleSave = () => flushChanges({ silent: false });

  // Clear Secondary Owner - empties every field in that section in one save,
  // logged to the Audit Trail as its own "Secondary Owner Cleared" entry.
  // Ownership goes back to 100 for the principal (withDerivedOwnership), and
  // the next Recreate Apps takes the second owner off the apps: an empty
  // Secondary Owner Name is the trigger, however it got emptied.
  const SECONDARY_OWNER_KEYS = CLIENT_INFO_SECTIONS.find(s => s.title === 'Secondary Owner').fields.map(f => f.key);
  const [clearingSecondary, setClearingSecondary] = useState(false);
  const hasSecondaryOwner = SECONDARY_OWNER_KEYS.some(k => String(form[k] ?? '').trim());
  const handleClearSecondaryOwner = async () => {
    if (!id || clearingSecondary || !hasSecondaryOwner) return;
    if (!window.confirm('Clear every Secondary Owner field on this client?')) return;
    setClearingSecondary(true);
    setSaveMsg('');
    try {
      // Anything else typed but not yet saved goes out first, as its own edit.
      if (autoSaveTimer.current) { clearTimeout(autoSaveTimer.current); autoSaveTimer.current = null; }
      for (let i = 0; i < 300 && isFlushingRef.current; i++) await new Promise(r => setTimeout(r, 100));
      await flushChanges({ silent: true });

      const cleared = Object.fromEntries(SECONDARY_OWNER_KEYS.map(k => [k, '']));
      const next = withDerivedOwnership({ ...formRef.current, ...cleared });
      const body = { ...cleared, ownershipPercent: next.ownershipPercent ?? '' };
      const res = await fetch(`${API}/clients-api/${id}`, {
        method: 'PATCH', headers: headersRef.current,
        body: JSON.stringify({ ...body, _editSource: 'clear-secondary-owner' }),
      });
      if (!res.ok) throw new Error('Save failed');
      formRef.current = next;
      setForm(next);
      onSavedRef.current(withRenewalDealName(next, clientRef.current));
      setSaveStatus('saved');
      setLastSavedAt(Date.now());
      setSaveMsg('✅ Secondary owner cleared - Recreate Apps to take them off the apps');
    } catch (e) {
      setSaveMsg(`Failed to clear the secondary owner: ${e.message}`);
    } finally {
      setClearingSecondary(false);
      setTimeout(() => setSaveMsg(''), 8000);
    }
  };

  // Recreate Apps - builds a fresh Submission App and Auto App from the
  // Client Information as it stands, including the Business Legal Name
  // (replaceBusinessName) and Business Description
  // (replaceBusinessDescription). Anything typed but not yet saved is saved first,
  // and a failed save stops it rather than regenerating from stale values.
  const [recreatingApps, setRecreatingApps] = useState(false);
  const handleRecreateApps = async () => {
    if (!id || recreatingApps) return;
    setRecreatingApps(true);
    setSaveMsg('');
    const waitForSaveToSettle = async () => {
      for (let i = 0; i < 300 && isFlushingRef.current; i++) await new Promise(r => setTimeout(r, 100));
    };
    try {
      // 1. Save first: cancel the pending debounce and send everything now,
      //    after any save already on its way has finished.
      if (autoSaveTimer.current) { clearTimeout(autoSaveTimer.current); autoSaveTimer.current = null; }
      await waitForSaveToSettle();
      const saved = await flushChanges({ silent: true });
      // A follow-up save can be queued by an edit made while the first was in flight.
      await waitForSaveToSettle();
      if (saved === false || lastFlushOkRef.current === false || isFlushingRef.current) {
        setSaveMsg('Failed to save Client Information - the apps were not recreated. Fix the problem and try again.');
        return;
      }

      // 2. Regenerate both edited apps from the saved record.
      const res = await fetch(`${API}/app-editor/generate/${id}`, {
        method: 'POST', headers: headersRef.current,
        body: JSON.stringify({
          force: true,
          replaceBusinessName: true,
          replaceBusinessDescription: true,
          // Recreate Apps maps the whole of Client Information onto both apps.
          replaceClientInfo: true,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d?.ok || d?.skipped) {
        setSaveMsg(`Failed to recreate apps: ${d?.reason || d?.error || `HTTP ${res.status}`}`);
        return;
      }
      setSaveMsg('✅ Submission App and Auto App recreated from Client Information');
      if (typeof onAppsRecreatedRef.current === 'function') onAppsRecreatedRef.current();
    } catch (e) {
      setSaveMsg(`Failed to recreate apps: ${e.message}`);
    } finally {
      setRecreatingApps(false);
      setTimeout(() => setSaveMsg(''), 8000);
    }
  };

  // Pulls Client Information fields (Business/Principal Owner/Secondary
  // Owner/Lender Entity Types) from the client's own completed DocuSign
  // envelope - not Zoho CRM. A manually-created client has no webhook
  // behind it to have populated these automatically the way a signed
  // application normally does; this reads the envelope whose ID the admin
  // enters (no searching by email) and runs it through the same
  // mapDocuSignTabs the webhook itself uses, so the result is identical to
  // what would have happened automatically if the envelope's own
  // completion had created this client.
  const [linkDataLoading, setLinkDataLoading] = useState(false);
  // The envelope to map, typed in by the admin - same flow as the standalone
  // DocuSign Envelope Mapper tool. Starts on the envelope already stored for
  // this client, so a known one is a single click; replacing it maps a
  // different envelope instead. The ID is printed at the top of every page
  // of the signed PDF ("Docusign Envelope ID: ...").
  const [envelopeInput, setEnvelopeInput] = useState(client?.envelopeId || '');
  const handleLinkData = async () => {
    if (!id || !envelopeInput.trim()) return;
    setLinkDataLoading(true);
    setSaveMsg('');
    try {
      const res = await fetch(`${API}/docusign/client-envelope-fields`, {
        method: 'POST', headers: headersRef.current,
        body: JSON.stringify({ clientId: id, envelopeId: envelopeInput.trim() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'No completed envelope found for this client');
      const updates = body.fields || {};
      // Business Description only fills an empty box - a description a rep
      // already wrote is kept over the application's.
      if (String(formRef.current?.businessDescription || '').trim()) delete updates.businessDescription;
      if (!Object.keys(updates).length) { setSaveMsg('No data found on that envelope'); return; }
      // Same as leaving the Business Legal Name box: the DBA written by Link
      // Data is the business name it ends up with.
      const linked = { ...formRef.current, ...updates };
      const linkedDba = dbaFollowingBusinessName(linked.businessName, linked.dba);
      if (linkedDba !== null) updates.dba = linkedDba;

      const filled = Object.keys(updates).length;
      // The envelope that was just mapped becomes this client's stored one,
      // so the next map (and anything else reading client.envelopeId) starts
      // from it.
      const mappedEnvelopeId = body.envelopeId || envelopeInput.trim();

      const saveRes = await fetch(`${API}/clients-api/${id}`, {
        method: 'PATCH', headers: headersRef.current,
        body: JSON.stringify({ ...updates, envelopeId: mappedEnvelopeId, _editSource: 'link-data' }),
      });
      if (!saveRes.ok) throw new Error('Save failed');
      // A renewal keeps its deal name: the envelope's legal name fills the
      // box, and the record (and the page heading) keep the "- N".
      const shown = renewalNumberOf(clientRef.current)
        ? { ...updates, ...(updates.businessName ? { businessName: withoutRenewalNumber(updates.businessName) } : {}), ...(updates.dba ? { dba: withoutRenewalNumber(updates.dba) || updates.dba } : {}) }
        : updates;
      setForm(prev => ({ ...prev, ...shown }));
      onSavedRef.current(withRenewalDealName({ ...formRef.current, ...shown, envelopeId: mappedEnvelopeId }, clientRef.current));
      setEnvelopeInput(mappedEnvelopeId);
      setSaveStatus('saved');
      setLastSavedAt(Date.now());
      setSaveMsg(`✅ Filled in ${filled} field${filled === 1 ? '' : 's'} from DocuSign envelope ${mappedEnvelopeId}`);
    } catch (e) {
      setSaveMsg(`Link failed: ${e.message}`);
    } finally {
      setLinkDataLoading(false);
      setTimeout(() => setSaveMsg(''), 5000);
    }
  };

  // Block save/submit if EIN or SSN contains all same digits or consecutive digits.
  const checkEinSsn = (val) => {
    const d = String(val ?? '').replace(/\D/g, '');
    if (d.length < 4) return false;
    const allSame = d.split('').every(c => c === d[0]);
    const isConsecutive = d.split('').every((c, i) => i === 0 || Number(c) === Number(d[i-1]) + 1);
    return allSame || isConsecutive;
  };
  const hasInvalidEinSsn = checkEinSsn(form.ein) || checkEinSsn(form.ownerSSN) || checkEinSsn(form.secondarySSN);
  // Same gate for the requested amount. It was flagged red in the label but
  // nothing stopped the save, so an amount the lender mappers silently
  // misparse (parseInt('75,000') === 75) went out anyway - the warning was
  // the only thing standing between a $75,000 request and a $75 one, and a
  // warning nobody has to clear is not a check. Mirrors amountProblem's own
  // test below, which is what draws the label red.
  const hasInvalidAmount = !!String(form.requestedAmount ?? '').trim()
    && !/^\d+(\.\d+)?$/.test(String(form.requestedAmount).trim());
  const blockSave = hasInvalidEinSsn || hasInvalidAmount;

  return (
    <div className="space-y-5">
      {CLIENT_INFO_SECTIONS.map(section => {
        // Same 3 sections missingClientInfoCount counts - Secondary Owner,
        // Portal & Deal, and Application IDs are legitimately blank on plenty
        // of real deals, so their fields never get flagged here either.
        const isRequiredSection = ['Business', 'Principal Owner', 'Lender Entity Types'].includes(section.title);
        return (
        <div key={section.title} className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5">
          {section.title === 'Secondary Owner' ? (
            <div className="flex items-center justify-between gap-3 mb-4">
              <p className="text-sm font-semibold text-gray-900">{section.title}</p>
              <button
                type="button"
                onClick={handleClearSecondaryOwner}
                disabled={clearingSecondary || !hasSecondaryOwner}
                title={hasSecondaryOwner ? 'Empty every Secondary Owner field' : 'No secondary owner on this client'}
                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {clearingSecondary ? 'Clearing…' : 'Clear Secondary Owner'}
              </button>
            </div>
          ) : (
          <p className={`text-sm font-semibold text-gray-900 ${section.note ? 'mb-1' : 'mb-4'}`}>{section.title}</p>
          )}
          {section.note && <p className="text-xs text-gray-500 mb-4">{section.note}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            {section.fields.map(f => {
              // Zoho Unique ID is required on its own, even though the rest of
              // Application IDs isn't - it's the visible field a rep can see
              // and correct that submitting to lenders prefers for finding the
              // Account to write the Lenders subform row and Stage_of_Package
              // to (see the field's own comment above; resolveZohoIdForSubform
              // server-side does still fall back to zohoId or an /Accounts/
              // link when this is blank, but this is the one enforced here).
              // The per-lender applicationId fields below stay legitimately
              // blank until that lender has actually been sent to once.
              // f.optional (e.g. Business Description) opts a field back out
              // of isRequiredSection even though its section is required.
              const isMissing = ((isRequiredSection && !f.optional) || f.key === 'zohoUniqueId') && !String(form[f.key] ?? '').trim();
              // EIN/SSN checks. Length first: every lender wants exactly 9
              // digits, and a missing/extra digit was passing silently here
              // only to be rejected by the lender at send time ("taxID must be
              // a numeric string of length 9"). Then the placeholder patterns
              // (all one digit, or a straight run like 123456789). The reason
              // goes in the label so the rep knows what to fix, not just that
              // something is off.
              // Business and Principal Phone: exactly 10 digits, same rule
              // Auto Submit enforces before Submit / Send to Lenders.
              const phoneProblem = (f.key === 'phone' || f.key === 'ownerPhone') ? (() => {
                const d = String(form[f.key] ?? '').replace(/\D/g, '');
                // 11 digits starting with 1 is a +1 country code - the server
                // drops it on save (stripUsCountryCode in clientStore.js).
                if (!d.length || d.length === 10 || (d.length === 11 && d[0] === '1')) return '';
                return `must be 10 digits (${d.length} entered)`;
              })() : '';
              const isEinSsn = f.key === 'ein' || f.key === 'ownerSSN' || f.key === 'secondarySSN';
              const einSsnProblem = isEinSsn ? (() => {
                const d = String(form[f.key] ?? '').replace(/\D/g, '');
                if (!d.length) return '';
                if (d.length !== 9) return `must be 9 digits (${d.length} entered)`;
                const allSame = d.split('').every(c => c === d[0]);
                const isConsecutive = d.split('').every((c, i) => i === 0 || Number(c) === Number(d[i-1]) + 1);
                return (allSame || isConsecutive) ? 'invalid value' : '';
              })() : '';
              // Requested Funding Amount has to be a bare number. Every lender
              // mapper runs it through parseInt/parseFloat, which fail quietly
              // and dangerously on anything else: parseInt('75,000') is 75, so
              // a comma turns a $75,000 request into a $75 one on its way to
              // the lender, and '$75000' parses to NaN. Digits with an
              // optional decimal only - a blank is left alone, since an empty
              // amount is a different problem from a mistyped one.
              const amountProblem = f.key === 'requestedAmount'
                && String(form[f.key] ?? '').trim()
                && !/^\d+(\.\d+)?$/.test(String(form[f.key]).trim())
                ? 'no characters allowed in requested funding amount'
                : '';
              // Business Start Date / Principal / Secondary DOB landing in the
              // current month and year - see isCurrentMonthYear's own comment.
              const isDateField = f.key === 'businessStartDate' || f.key === 'ownerDOB' || f.key === 'secondaryDOB';
              // A Business Start Date inside the last six months. Most often a
              // mistyped year (2026 for 2016), and when it is real it is a deal
              // almost no lender will fund - either way it is worth seeing
              // before the deal goes anywhere.
              //
              // Only the start date, never the DOBs. yearsSince already returns
              // null for a blank, an unparseable value or a future date, so a
              // half-typed entry stays quiet instead of flashing red on every
              // keystroke.
              const yearsOpen = f.key === 'businessStartDate' ? yearsSince(form[f.key]) : null;
              const monthsOpen = yearsOpen == null ? null : yearsOpen * 12;
              const dateProblem = isDateField && isCurrentMonthYear(form[f.key])
                ? 'cannot be the current month and year'
                : (monthsOpen != null && monthsOpen < 6)
                ? `under 6 months in business (${monthsOpen < 1 ? 'under 1 month' : Math.round(monthsOpen) + (Math.round(monthsOpen) === 1 ? ' month' : ' months')}) — check the year`
                : '';
              // State fields have to be the 2-letter abbreviation, all caps -
              // "New York" or "ny" both pass a human reading the form but fail
              // whatever downstream expects "NY" exactly. Blank is left alone,
              // same as amountProblem/dateProblem above - a missing state is a
              // different problem, already covered by isMissing.
              const isStateField = f.key === 'state' || f.key === 'ownerState' || f.key === 'secondaryState';
              // Skipped entirely on a Canadian deal - "B.C.", "Ontario", "QC"
              // are all real answers there, and this US-only 2-letter format
              // was rejecting every one of them. Business State (form.state)
              // is what decides US-vs-Canada, same field AutoSubmit's own
              // send-blocking version of this check keys off.
              const stateProblem = isStateField
                // Canadian per Auto Submit's State dropdown - the same value
                // the send check and the lender routing use - so this red
                // text and the send block always agree. Business State is
                // only the fallback for a client not yet through Auto Submit.
                && !(client?.dealCriteria?.state ? looksCanadian(client.dealCriteria.state) : looksCanadian(form.state))
                && String(form[f.key] ?? '').trim()
                && !/^[A-Z]{2}$/.test(String(form[f.key]).trim())
                ? 'must be a 2-letter abbreviation, all caps (e.g. NY)'
                : '';
              const isInvalid = isMissing || !!einSsnProblem || !!amountProblem || !!dateProblem || !!stateProblem || !!phoneProblem;
              return (
              <div key={f.key}>
                <label className={`block text-[11px] font-medium mb-1 ${isInvalid ? 'text-red-500' : 'text-gray-500'}`}>
                  {f.label}{isMissing && ' *'}{f.computedNote && (form.requestedAmountManual === true
                    ? <span className="text-gray-400 font-normal"> — edited · <button type="button" onClick={() => resetToFormula(f)} className="text-blue-500 hover:text-blue-600 hover:underline">reset to {f.computedNote}</button></span>
                    : <span className="text-gray-400 font-normal"> — pre-filled as {f.computedNote}, editable</span>)}{einSsnProblem && ` — ${einSsnProblem}`}{amountProblem && ` — ${amountProblem}`}{dateProblem && ` — ${dateProblem}`}{stateProblem && ` — ${stateProblem}`}{phoneProblem && ` — ${phoneProblem}`}
                </label>
                {f.type === 'select' ? (
                  <select
                    value={form[f.key] ?? ''}
                    onChange={e => setField(f.key, e.target.value)}
                    onBlur={handleBlur}
                    disabled={f.fixed}
                    className={`w-full text-gray-900 text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all disabled:opacity-70 disabled:cursor-not-allowed border ${isInvalid ? 'bg-red-50 border-red-300' : 'bg-gray-50 border-gray-200'}`}
                  >
                    {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <div className="relative">
                    {f.prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{f.prefix}</span>}
                    <input
                      type="text"
                      value={form[f.key] ?? ''}
                      onChange={e => setField(f.key, e.target.value)}
                      onBlur={f.key === 'businessName' ? handleBusinessNameBlur : handleBlur}
                      title={f.computedFrom ? `Pre-filled as ${f.computedNote}. Type to set your own amount.` : undefined}
                      className={`w-full text-gray-900 text-sm rounded-xl py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all border ${f.prefix ? 'pl-7 pr-3' : 'px-3'} ${isInvalid ? 'bg-red-50 border-red-300' : 'bg-gray-50 border-gray-200'}`}
                    />
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
        );
      })}

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving || blockSave}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm px-5 py-2.5 rounded-xl font-medium shadow-sm transition-colors"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        {/* Envelope ID, then the green Docusign Mapper button - the same flow
            as the standalone DocuSign Envelope Mapper tool. Maps exactly the
            envelope entered (pre-filled with the stored one) into Client
            Information - not a Zoho CRM lookup. */}
        <input
          type="text"
          value={envelopeInput}
          onChange={e => setEnvelopeInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && envelopeInput.trim() && !linkDataLoading) handleLinkData(); }}
          placeholder="DocuSign Envelope ID"
          title='The ID printed at the top of every page of the signed application ("Docusign Envelope ID: ...")'
          className="w-80 bg-gray-50 border border-gray-200 text-gray-900 text-sm font-mono rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
        />
        <button
          onClick={handleLinkData}
          disabled={linkDataLoading || !envelopeInput.trim()}
          title="Fill in Client Information from the DocuSign envelope entered to the left"
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm px-5 py-2.5 rounded-xl font-medium shadow-sm transition-colors"
        >
          {linkDataLoading ? <RefreshCw size={14} className="animate-spin" /> : null}
          {linkDataLoading ? 'Mapping…' : 'Docusign Mapper'}
        </button>
        <button
          onClick={handleRecreateApps}
          disabled={recreatingApps || saving || blockSave}
          title="Save Client Information, then rebuild the Submission App and Auto App from it"
          className="flex items-center gap-2 bg-white hover:bg-gray-50 disabled:opacity-50 text-gray-700 text-sm px-5 py-2.5 rounded-xl font-medium border border-gray-200 shadow-sm transition-colors"
        >
          {recreatingApps ? <RefreshCw size={14} className="animate-spin" /> : null}
          {recreatingApps ? 'Recreating…' : 'Recreate Apps'}
        </button>
        {hasInvalidEinSsn && (
          <span className="text-sm font-medium text-red-500">EIN or SSN contains invalid digits — please correct before saving</span>
        )}
        {hasInvalidAmount && (
          <span className="text-sm font-medium text-red-500">Requested funding amount must be digits only — please correct before saving</span>
        )}
        {saveMsg ? (
          <span className={`text-sm font-medium ${/^(Failed|Link failed|No Zoho|No data|No new data|No business)/.test(saveMsg) ? 'text-red-500' : 'text-green-600'}`}>{saveMsg}</span>
        ) : (
          // Autosave status - covers blur and the typing-pause debounce, which
          // never touch saveMsg (that's reserved for an explicit Save click).
          // Silent otherwise, so a rep tabbing through fields has some
          // confirmation without a message popping up and fading on every one.
          <span className="text-xs text-gray-400 flex items-center gap-1.5">
            {saveStatus === 'saving' && <><RefreshCw size={11} className="animate-spin" /> Saving…</>}
            {saveStatus === 'saved' && lastSavedAt && <>Saved {new Date(lastSavedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</>}
            {saveStatus === 'error' && <span className="text-red-500">Autosave failed — click Save</span>}
          </span>
        )}
      </div>
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { can, user, token } = useAuth();
  const { t } = useLanguage();
  // ?tab=statements (or ?category=<id>) lands directly on that document folder.
  const initialCategory = (() => {
    const p = new URLSearchParams(search);
    if (p.get('tab')?.toLowerCase() === 'statements') return 'bank_statements';
    const c = p.get('category');
    return DOC_CATEGORIES.some(x => x.id === c) ? c : DOC_CATEGORIES[0].id;
  })();
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [selectedBankAccount, setSelectedBankAccount] = useState('');
  const [customAccounts, setCustomAccounts] = useState([]);

  // Read ?tab= from URL to deep-link into a specific tab
  const TAB_PARAM_MAP = {
    credit:     'Documents', // opens Documents tab with credit panel expanded
    autosubmit: 'Auto Submit',
    documents:  'Documents',
    statements: 'Documents', // opens Documents tab on the Bank Statements folder
    underwriting: 'Underwriting',
    seasonality: 'Seasonality',
    activity:   'Activity',
    notes:      'Notes',
    clientinfo: 'Client Information',
    requests:   'Requests',
    audit:      'Audit Trail',
  };
  const tabParam = new URLSearchParams(search).get('tab')?.toLowerCase() || '';
  const initialTab = TAB_PARAM_MAP[tabParam] || 'Documents';
  const [activeTab, setActiveTab] = useState(
    !can.seeSeasonalityUnderwriting && ['Seasonality', 'Underwriting'].includes(initialTab) ? 'Documents' : initialTab
  );
  const [creditPanelFromUrl] = useState(tabParam === 'credit');
  // The drop area is open on arrival rather than hidden behind the Upload
  // button. Uploading is what a rep comes to a document folder to do, so the
  // target for a dragged file is there waiting instead of costing a click that
  // only reveals it. The button still collapses it for anyone who wants the
  // document list to itself.
  const [showUpload, setShowUpload] = useState(true);
  // Every folder opens with its own drop area showing. Without this a collapse
  // in one folder would follow the rep into all the others, which is the
  // opposite of the point. Declared after the state it sets, deliberately - a
  // hook placed above its own useState reads fine and still runs, but it is a
  // trap for the next edit.
  useEffect(() => { setShowUpload(true); }, [selectedCategory]);
  const [emptyingTrash, setEmptyingTrash] = useState(false);
  const [realDocs, setRealDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [requestingSent, setRequestingSent] = useState({});
  const [notification, setNotification] = useState(null);
  const [apiClient, setApiClient] = useState(null);
  const [showCustomRequest, setShowCustomRequest] = useState(false);
  // The missing-documents list starts closed. The count is the part a rep
  // needs on sight; the breakdown is what they open when they are about to do
  // something about it, and left open it pushed the tabs and the documents
  // themselves below the fold on any deal missing more than a couple.
  const [showMissingDocs, setShowMissingDocs] = useState(false);
  const [customReqForm, setCustomReqForm] = useState({ category: '', instructions: '', dueDate: '' });
  const [sendingCustom, setSendingCustom] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [clientActivity, setClientActivity] = useState([]);
  const [ocrResults, setOcrResults] = useState([]);
  const [ocrSending, setOcrSending] = useState(false);
  const [ocrStatus, setOcrStatus] = useState('');
  const [docFinancials, setDocFinancials] = useState(null);
  const [selectedBankType, setSelectedBankType] = useState('');
  const [ocrBankSending, setOcrBankSending] = useState(false);
  const [ocrBankStatus, setOcrBankStatus] = useState('');
  const [creditPulling, setCreditPulling] = useState(false);
  const [creditResult, setCreditResult]   = useState(null);
  // The last pull's outcome, read back from the saved reports. Without this the
  // lock notice only existed for as long as the tab stayed open - reload the
  // page and a frozen file looked like a deal nobody had tried.
  const [creditLock, setCreditLock] = useState(null);
  // The second owner's most recent scored report, when this deal has one. The
  // principal's score already has a home in the header; this is the other
  // person on the same deal, which had nowhere to appear at all.
  const [secondOwnerReport, setSecondOwnerReport] = useState(null);
  useEffect(() => {
    if (!id) return;
    const t = localStorage.getItem('mca_token');
    if (!t) return;
    fetch(`${API}/credit/reports/${id}`, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => (r.ok ? r.json() : []))
      .then(rows => {
        const list = Array.isArray(rows) ? rows : [];
        // Newest first: an old freeze that has since scored must not keep warning.
        const newestFirst = list.slice().sort((a, b) =>
          String(b.pulledAt || '').localeCompare(String(a.pulledAt || '')));
        // The freeze warning is about the PRINCIPAL. Rows written before owners
        // existed carry no owner and are the principal's, which is what the
        // server already reports them as.
        const latest = newestFirst.filter(r => (r.owner || 'primary') !== 'secondary')[0];
        setCreditLock(latest && latest.locked ? latest : null);
        setSecondOwnerReport(newestFirst.find(r => r.owner === 'secondary' && r.score != null) || null);
      })
      .catch(() => {});
  }, [id]);
  const [creditSsn, setCreditSsn]         = useState('');
  const [creditFirstName, setCreditFirstName] = useState('');
  const [creditLastName, setCreditLastName]   = useState('');
  const [creditDob, setCreditDob]             = useState('');
  const [creditStreet, setCreditStreet]       = useState('');
  const [creditCity, setCreditCity]           = useState('');
  const [creditZip, setCreditZip]             = useState('');
  const [creditPrefilling, setCreditPrefilling] = useState(false);
  const [showCreditPanel, setShowCreditPanel] = useState(creditPanelFromUrl);
  const [showPingConfirm, setShowPingConfirm] = useState(false);
  const [pinging, setPinging]             = useState(false);
  // Renewal button (handleRenewal below). Up here with the other state: a
  // hook declared below the early returns crashed the page on load.
  const [renewing, setRenewing] = useState(false);
  // The deal name in the page heading - super admins double-click it to edit.
  // This is the DEAL's name ("Acme LLC - 3"), saved exactly as typed; it is
  // where a renewal's number is added, changed or removed. Business Legal
  // Name and DBA in Client Information stay without the number, and those
  // are what lenders receive.
  const canEditDealName = isSuperAdmin(user);
  const [dealNameDraft, setDealNameDraft] = useState(null); // null = not editing
  const [dealNameSaving, setDealNameSaving] = useState(false);
  const saveDealName = async () => {
    if (dealNameDraft === null || dealNameSaving) return;
    const next = dealNameDraft.replace(/\s+/g, ' ').trim();
    if (!next || next === String(client?.businessName || '').trim()) { setDealNameDraft(null); return; }
    setDealNameSaving(true);
    try {
      // A name ending in a number ("- 3") is a renewal: Deal Type goes to
      // Renewal with it, so Link Data cannot strip the number again.
      const endsInNumber = /\s*-\s*\d+$/.test(next);
      const res = await fetch(`${API}/clients-api/${id}`, {
        method: 'PATCH', headers: getHeaders(),
        body: JSON.stringify({ businessName: next, ...(endsInNumber ? { orbitDealType: 'Renewal' } : {}), _dealNameEdit: true, _editSource: 'manual' }),
      });
      const saved = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(saved?.error || `HTTP ${res.status}`);
      setApiClient(prev => ({ ...(prev || {}), businessName: saved.businessName || next, ...(saved.orbitDealType ? { orbitDealType: saved.orbitDealType } : {}) }));
      setDealNameDraft(null);
      setNotification(`Deal name changed to "${saved.businessName || next}"`);
    } catch (e) {
      setNotification(`Could not rename the deal: ${e.message}`);
    } finally {
      setDealNameSaving(false);
    }
  };

  const mockClient = getClientById(id);
  const client = mockClient || apiClient;
  const rep = client ? getRepById(client.assignedRepId) : null;

  // Count of blank fields across Business, Principal Owner, and Lender Entity
  // Types only - the sections a rep is actually expected to keep filled in.
  // Secondary Owner, Portal & Deal, and Application IDs are excluded: those
  // are legitimately blank on plenty of real deals (no second owner, IDs that
  // only exist once a lender's actually been submitted to), so counting them
  // would flag most clients as "incomplete" for reasons nobody can fix.
  // Plus Zoho Unique ID - the one Application IDs field that IS required
  // (Auto Submit won't send without it; see the field's own isMissing check),
  // so a blank one turns the tab red too rather than only surfacing on
  // Auto Submit. The rest of Application IDs stays out, for the reason above.
  const missingClientInfoCount = client
    ? CLIENT_INFO_SECTIONS
        .filter(s => ['Business', 'Principal Owner', 'Lender Entity Types'].includes(s.title))
        .reduce((n, s) => n + s.fields.filter(f => !f.optional && !String(client[f.key] ?? '').trim()).length, 0)
      + (String(client.zohoUniqueId ?? '').trim() ? 0 : 1)
    : 0;

  const getHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('mca_token') || token || ''}`,
  });
  const headers = getHeaders();

  // Quick-jump client search - the same "Search clients..." box Clients.jsx
  // has, now also available from inside a client so a rep can jump straight
  // to another one without going back to the list first. Named `jump*`
  // rather than `search*` because `search` is already taken above by
  // useLocation's querystring.
  //
  // The full client list is fetched lazily, once, on first focus - not on
  // every page load - since this page is opened far more often than the
  // list is actually used, and it would otherwise mean pulling all ~4,700
  // client records down on every single client detail view.
  const [jumpQuery, setJumpQuery] = useState('');
  const [jumpOpen, setJumpOpen] = useState(false);
  const [jumpAllClients, setJumpAllClients] = useState(null); // null = not yet loaded
  const [jumpLoading, setJumpLoading] = useState(false);

  const loadJumpClients = () => {
    if (jumpAllClients !== null || jumpLoading) return;
    setJumpLoading(true);
    fetch(`${API}/clients-api`, { headers: getHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => setJumpAllClients(Array.isArray(data) ? data : []))
      .catch(() => setJumpAllClients([]))
      .finally(() => setJumpLoading(false));
  };

  // Same matcher Clients.jsx uses (see utils/clientSearch.js), so a query
  // that finds a client on the list page finds it here too. Excludes the
  // client already open - jumping to yourself isn't useful - and caps the
  // dropdown at 8 so a broad query (e.g. a common first name) stays
  // scannable instead of dumping hundreds of rows under the input.
  const jumpResults = useMemo(() => {
    const q = jumpQuery.trim();
    if (!q || !jumpAllClients) return [];
    return jumpAllClients
      .filter(c => c.id !== id && matchesClientSearch(c, q))
      .slice(0, 8);
  }, [jumpQuery, jumpAllClients, id]);

  // Fetch client from API if not in mock data
  useEffect(() => {
    if (mockClient || !id) return;
    fetch(`${API}/clients-api/${id}`, { headers: getHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setApiClient(data); })
      .catch(() => {});
  }, [id, mockClient]);

  const [statementTimeline, setStatementTimeline] = useState([]);

  // Fetch financials + documents in parallel (previously sequential, and the
  // document list was fetched three separate times across two effects — this
  // was the main source of the multi-second delay opening a client).
  useEffect(() => {
    if (!id) return;
    setLoadingDocs(true);
    const h = getHeaders();

    const financialsPromise = fetch(`${API}/documents/financials/${id}`)
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);
    const docsPromise = fetch(`${API}/documents/client/${id}`, { headers: h })
      .then(r => r.ok ? r.json() : [])
      .catch(() => []);

    docsPromise
      .then(docs => setRealDocs(docs))
      .finally(() => setLoadingDocs(false));

    Promise.all([financialsPromise, docsPromise]).then(([data, docs]) => {
      const bs = docs.filter(d => d.category === 'bank_statements' && d.extractedFinancials && !d.extractedFinancials.error);

      if (data && data.available) {
        setDocFinancials(data);
        if (bs.length > 0) setStatementTimeline(buildTimeline(bs));
        return;
      }

      // Fallback: aggregate per-doc extractedFinancials
      if (bs.length === 0) return;
      const timeline = buildTimeline(bs);
      setStatementTimeline(timeline);
      let totalCredits = 0, totalDebits = 0, totalLenderDebits = 0, totalNsf = 0, totalPos = 0;
      let avgDailyBalSum = 0, avgDailyBalCount = 0, totalDeposits = 0;
      const lenderMap = {};
      bs.forEach(d => {
        const f = d.extractedFinancials;
        totalCredits += f.credits || 0;
        totalDebits += f.debits || 0;
        totalLenderDebits += f.lender_debits || 0;
        totalNsf += f.nsf_count || 0;
        totalPos += f.pos_count || 0;
        totalDeposits += f.deposit_count || f.num_deposits || 0;
        if (f.avg_daily_balance) { avgDailyBalSum += f.avg_daily_balance; avgDailyBalCount++; }
        (f.lender_list || []).forEach(name => {
          const key = name.toUpperCase().trim();
          if (!lenderMap[key]) lenderMap[key] = { name: key, totalPaid: 0, occurrences: 0, perMonth: [] };
          lenderMap[key].occurrences++;
          lenderMap[key].perMonth.push({ period: extractPeriod(d.fileName), amount: (f.lender_debits || 0) / (f.lender_list || []).length });
        });
      });
      const lenders = Object.values(lenderMap);
      if (lenders.length > 0 && totalLenderDebits > 0) {
        // Distribute proportionally by occurrence
        const totalOcc = lenders.reduce((s, l) => s + l.occurrences, 0);
        lenders.forEach(l => { l.totalPaid = Math.round(totalLenderDebits * (l.occurrences / totalOcc)); });
      }
      const months = bs.length;
      const avgMonthly = Math.round(totalCredits / months);
      const withholdingRate = totalCredits > 0 ? Math.round((totalLenderDebits / totalCredits) * 1000) / 10 : 0;
      setDocFinancials({
        available: true,
        monthsCovered: months,
        avgMonthlyRevenue: avgMonthly,
        estimatedAnnualRevenue: avgMonthly * 12,
        numberOfDeposits: totalDeposits || months,
        negativeDays: totalNsf,
        totalCredits: Math.round(totalCredits),
        positions: lenders,
        positionCount: lenders.length,
        totalLenderPayments: Math.round(totalLenderDebits),
        withholdingRate,
        docsProcessed: bs.length,
        pendingDocs: 0,
        avgDailyBalance: avgDailyBalCount > 0 ? Math.round(avgDailyBalSum / avgDailyBalCount) : 0,
        totalDebits: Math.round(totalDebits),
        nsfCount: totalNsf,
        posCount: totalPos,
      });
    });

    fetch(`${API}/activity`, { headers: h })
      .then(r => r.ok ? r.json() : [])
      .then(data => setClientActivity(data.filter(a => a.clientId === id)))
      .catch(() => setClientActivity([]));

    fetch(`${API}/ocr/results/${id}`, { headers: h })
      .then(r => r.ok ? r.json() : [])
      .then(data => setOcrResults(data))
      .catch(() => setOcrResults([]));

    fetch(`${API}/clients-api/requests/all`, { headers: h })
      .then(r => r.ok ? r.json() : [])
      .then(data => setPendingRequests(data.filter(r => r.clientId === id && r.status !== 'fulfilled')))
      .catch(() => {});
  }, [id]);

  // If the URL contains ?openAutoSubmit=1, switch to the Auto Submit tab automatically
  // (`search` is already destructured from useLocation() at the top of the component)
  const _params = new URLSearchParams(search);
  const openAutoSubmit = _params.get('openAutoSubmit') === '1';
  // "Modify Deal" links carry edit=1: prefill the form but stay on it rather
  // than opening the review popup that "Preview Deal" wants.
  const autoSubmitEditOnly = _params.get('edit') === '1';
  useEffect(() => {
    try {
      const params = new URLSearchParams(search);
      if (params.get('openAutoSubmit') === '1') {
        // Delay briefly to ensure client state is ready
        setTimeout(() => setActiveTab('Auto Submit'), 50);
      }
    } catch (e) {
      // no-op
    }
  }, [search]);

  // ?tab=statements arriving after mount (e.g. an in-app link) � same effect as
  // the initial-state path above, for when the component is already rendered.
  useEffect(() => {
    try {
      if (new URLSearchParams(search).get('tab')?.toLowerCase() === 'statements') {
        setActiveTab('Documents');
        setSelectedCategory('bank_statements');
      }
    } catch { /* no-op */ }
  }, [search]);

  if (!client) {
    return <div className="p-6 text-gray-400">Loading client...</div>;
  }

  // Merge real docs with mock docs (mock docs show until replaced by real ones)
  const allDocs = realDocs.length > 0 ? realDocs : [];

  // Count of unmet document requirements - Application, 3+ Bank Statements,
  // Submission App (admin_app_ne), and Auto App (admin_app). Drives both the
  // Documents tab's red flag/count and the automatic "Missing Docs" status
  // the server computes on every upload/delete (see documents.js) - kept in
  // the same 4-requirement shape here so the tab agrees with what actually
  // sets that status, not a separate approximation of it.
  const REQUIRED_DOC_REQUIREMENTS = [
    { category: 'application',     min: 1 },
    { category: 'bank_statements', min: 3 },
    { category: 'admin_app_ne',    min: 1 }, // Submission App
    { category: 'admin_app',       min: 1 }, // Auto App
  ];
  const docCountByCategory = allDocs.reduce((acc, d) => {
    if (d.category !== 'trash') acc[d.category] = (acc[d.category] || 0) + 1;
    return acc;
  }, {});
  const missingDocRequirementsCount = REQUIRED_DOC_REQUIREMENTS
    .filter(r => (docCountByCategory[r.category] || 0) < r.min).length;

  // Compute missing categories from real docs
  const uploadedCategories = new Set(allDocs.map(d => d.category));
  // Trash is not a document anyone can produce, so it never belongs on this
  // list. It is a destination for deleted files, and nothing is ever uploaded
  // into it - which meant it counted as missing on every client, forever, and
  // offered a rep the option of asking a merchant to send their trash.
  //
  // Only this list is filtered. The Trash folder itself, what it holds and how
  // documents move in and out of it are all untouched.
  const missingCategories = DOC_CATEGORIES.filter(c => c.id !== 'trash' && !uploadedCategories.has(c.id));

  // Deleted statements still come back from /documents/client, so they are
  // excluded here � clearing out a bad upload would otherwise leave the client
  // looking covered when nothing usable is actually on file.
  const hasBankStatements = allDocs.some(d => d.category === 'bank_statements' && d.status !== 'Deleted');

  // Filter by selected category
  const categoryDocs = allDocs.filter(d => d.category === selectedCategory
    && (selectedCategory !== 'bank_statements' || !selectedBankAccount || d.bankAccount === selectedBankAccount)
  );
  const bankAccountsInDocs = [...new Set([...allDocs.filter(d => d.category === 'bank_statements' && d.bankAccount).map(d => d.bankAccount), ...customAccounts])];
  const allBankAccounts = bankAccountsInDocs.length > 0 ? bankAccountsInDocs : (allDocs.some(d => d.category === 'bank_statements') ? ['Account 1'] : []);

  const addBankAccount = () => {
    const next = `Account ${allBankAccounts.length + 1}`;
    setCustomAccounts(prev => [...prev, next]);
    setSelectedBankAccount(next);
  };
  const visibleDocs = can.seeInternalDocs
    ? categoryDocs
    : categoryDocs.filter(d => d.visibility !== 'internal');

  const catLabel = DOC_CATEGORIES.find(c => c.id === selectedCategory)?.label || '';
  // The open folder's ancestors, outermost first, when it is a subfolder -
  // e.g. JRW App -> [Additional Submission Docs, Equipment]. The outermost is
  // what the sidebar highlights; the breadcrumb links back to each of them.
  const selectedCategoryAncestors = (() => {
    const chain = [];
    let cur = DOC_CATEGORIES.find(c => c.id === selectedCategory);
    while (cur?.parent && chain.length < 5) {
      const parent = DOC_CATEGORIES.find(c => c.id === cur.parent);
      if (!parent) break;
      chain.unshift(parent);
      cur = parent;
    }
    return chain;
  })();
  const selectedCategoryRoot = selectedCategoryAncestors[0]?.id || '';

  // Send custom request
  const sendCustomRequest = async (e) => {
    e.preventDefault();
    if (!customReqForm.category) return;
    setSendingCustom(true);
    const cat = DOC_CATEGORIES.find(c => c.id === customReqForm.category);
    try {
      await fetch(`${API}/clients-api/${id}/reminder`, {
        method: 'POST', headers,
        body: JSON.stringify({ category: customReqForm.category, categoryLabel: cat?.label, customMessage: customReqForm.instructions || `Please upload your ${cat?.label} at your earliest convenience.` }),
      });
      const newReq = { id: `req_${Date.now()}`, category: customReqForm.category, catLabel: cat?.label, instructions: customReqForm.instructions, dueDate: customReqForm.dueDate, status: 'Pending', createdAt: new Date().toISOString() };
      setPendingRequests(prev => [newReq, ...prev]);
      setNotification(`Request sent to ${client.email} for ${cat?.label}`);
    } catch {
      setNotification(`Request queued for ${cat?.label}`);
    }
    setSendingCustom(false);
    setShowCustomRequest(false);
    setCustomReqForm({ category: '', instructions: '', dueDate: '' });
    setTimeout(() => setNotification(null), 4000);
  };

  const fulfillRequest = (reqId) => setPendingRequests(prev => prev.filter(r => r.id !== reqId));

  // Send missing doc request email
  const sendDocRequest = async (category) => {
    const cat = DOC_CATEGORIES.find(c => c.id === category);
    setRequestingSent(prev => ({ ...prev, [category]: 'sending' }));
    try {
      await fetch(`${API}/clients-api/${id}/reminder`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          category: cat.id,
          categoryLabel: cat.label,
          instructions: `Please upload your ${cat.label} at your earliest convenience.`,
        }),
      });
      setRequestingSent(prev => ({ ...prev, [category]: 'sent' }));
      setNotification(`✈️ Request sent to ${client.email} for ${cat.label}`);
      setTimeout(() => setNotification(null), 4000);
    } catch {
      setRequestingSent(prev => ({ ...prev, [category]: 'sent' }));
      setNotification(`Request queued for ${cat.label}`);
      setTimeout(() => setNotification(null), 4000);
    }
  };

  // Fetches the prefill and writes it into the panel's fields. Returns what it
  // filled rather than only setting state, because Pull Credit now runs this
  // itself and has to post the values in the same tick - reading them back out
  // of state would read the render before this one and post the empty strings
  // the panel had a moment ago.
  // preferExisting: keep what is already in the box and only fill blanks.
  //
  // Pull Credit prefills before it pulls, and the prefill used to win every
  // field - so correcting the surname and clicking Pull Credit re-fetched the
  // mapped value and pulled with that instead. The correction was discarded by
  // the very click meant to use it, and there was no way to pull with an edited
  // field at all.
  //
  // Prefill from Application still overwrites, because replacing what is there
  // is the whole point of pressing it.
  const runPrefill = async ({ silent = false, preferExisting = false } = {}) => {
    const res = await fetch(`${API}/credit/prefill/${id}`, { headers });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'No parsed application found');
    // Under preferExisting a non-empty typed value wins; otherwise the fetched
    // one does, which is the old behaviour unchanged.
    const pick = (mine, theirs) => (preferExisting
      ? (String(mine || '').trim() ? mine : theirs)
      : (theirs || mine));
    const filled = {
      firstName:   pick(creditFirstName, data.firstName),
      lastName:    pick(creditLastName,  data.lastName),
      dob:         pick(creditDob,       data.dob),
      // Digits only. The server already strips, but a hyphenated SSN typed
      // straight into the panel has to be cleaned on this side too - Experian
      // rejects "123-45-6789" and the pull never even reaches it.
      // The SSN is corrigible on the same terms: a rep who types the right one
      // over a wrong mapping must not have it replaced by that same wrong
      // mapping on the click that pulls.
      ssn:         String(pick(creditSsn, data.ssn) || '').replace(/\D/g, '').slice(0, 9),
      streetLine1: pick(creditStreet, data.streetLine1),
      city:        pick(creditCity,   data.city),
      zip:         pick(creditZip,    data.zip),
    };
    setCreditFirstName(filled.firstName);
    setCreditLastName(filled.lastName);
    setCreditDob(filled.dob);
    setCreditSsn(filled.ssn);
    setCreditStreet(filled.streetLine1);
    setCreditCity(filled.city);
    setCreditZip(filled.zip);
    if (!silent) {
      setNotification(`✅ Prefilled from application — DOB: ${data.dob || 'not found'}`);
      setTimeout(() => setNotification(null), 3000);
    }
    return filled;
  };

  /**
   * Load the SECOND owner's details into the credit panel.
   *
   * Fills only - it does not pull. The pull itself is keyed on the client, not
   * on a person: claimCreditPull(clientId) reuses any report for this client
   * from the last reuse window, so a second-owner pull on a deal that already
   * has a recent report would hand back the FIRST owner's score as though it
   * were the second owner's, and forcing past that would append a report to the
   * same client with nothing recording whose it is. Neither is a thing to do
   * quietly to credit data. So this gets the seven fields into the panel - the
   * whole of the retyping - and the rep presses "Pull with these fields", which
   * pulls exactly what is in the boxes.
   *
   * Client record first, application tab data second: the record is what a rep
   * has already corrected, the application is what was signed.
   */
  // Whose details the button loads, and whether there is anyone to load. Read
  // once here so the button's label, its tooltip and the fill itself cannot
  // disagree about whether this deal has a second owner.
  const secondOwnerName = String(
    client?.secondaryOwnerName || client?.tabData?.['secondary name'] || ''
  ).trim();

  const fillFromSecondOwner = () => {
    const t = client?.tabData || {};
    const pick = (...vals) => {
      for (const v of vals) { const s = String(v ?? '').trim(); if (s) return s; }
      return '';
    };
    const full = pick(client?.secondaryOwnerName, t['secondary name']);
    const parts = full.split(/\s+/).filter(Boolean);
    const first = parts[0] || '';
    const last = parts.slice(1).join(' ');

    setCreditFirstName(first);
    setCreditLastName(last);
    setCreditDob(pick(client?.secondaryDOB, t['secondary dob']));
    setCreditSsn(pick(client?.secondarySSN, t['secondary ssn']).replace(/\D/g, '').slice(0, 9));
    setCreditStreet(pick(client?.secondaryAddress, t['secondary address']));
    setCreditCity(pick(client?.secondaryCity, t['secondary city']));
    setCreditZip(pick(client?.secondaryZip, t['secondary zip']));

    // Returned as well as set, so a pull in the same click can post these
    // values instead of reading state that has not re-rendered yet - the same
    // reason runPrefill returns what it filled.
    const filled = {
      firstName: first, lastName: last,
      dob: pick(client?.secondaryDOB, t['secondary dob']),
      ssn: pick(client?.secondarySSN, t['secondary ssn']).replace(/\D/g, '').slice(0, 9),
      streetLine1: pick(client?.secondaryAddress, t['secondary address']),
      city: pick(client?.secondaryCity, t['secondary city']),
      zip: pick(client?.secondaryZip, t['secondary zip']),
    };

    // Said plainly rather than left to look like a dead button: a second owner
    // named but with no SSN on file cannot be pulled, and "nothing happened" is
    // the worst possible way to learn that.
    if (!full) {
      setNotification('No second owner on this client');
    } else if (filled.ssn.length !== 9) {
      setNotification(`${full} has no SSN on file — add it under Secondary Owner on Client Information, then pull`);
    } else {
      setNotification(`Loaded ${full} — pulling their credit now`);
    }
    setTimeout(() => setNotification(null), 6000);
    return full && filled.ssn.length === 9 ? filled : null;
  };

  /**
   * Fill from the second owner and pull, in one click.
   *
   * The pull carries owner: 'secondary', which is what keeps this from
   * colliding with the principal's report - the server scopes both the reuse
   * window and the stored row to the person, so this neither hands back the
   * principal's score nor overwrites it.
   *
   * The values are posted directly rather than read back out of state: the
   * fields above have been set in this same tick and the component has not
   * re-rendered, so state still holds what was in the boxes a moment ago.
   */
  const pullSecondOwnerCredit = async () => {
    const filled = fillFromSecondOwner();
    if (!filled) return;
    await pullCredit({ usePanelOnly: true, piiOverride: filled, owner: 'secondary' });
  };

  const prefillFromApplication = async () => {
    setCreditPrefilling(true);
    try {
      await runPrefill();
    } catch (e) {
      setNotification(`⚠️ Prefill failed: ${e.message}`);
      setTimeout(() => setNotification(null), 4000);
    }
    setCreditPrefilling(false);
  };

  /**
   * Two ways to pull, and the difference is only where the values come from.
   *
   *   usePanelOnly = false  (Pull Credit)
   *     One click: fill any blank from the application, keep anything typed,
   *     then pull. Prefill used to be a separate button a rep had to remember
   *     to press first, and forgetting it meant a disabled button with no
   *     explanation.
   *
   *   usePanelOnly = true   (Pull with these fields)
   *     Pulls exactly what is in the boxes and does not call the prefill at
   *     all. This exists because "it kept what I typed" and "it never went
   *     and looked" are different guarantees: when the application's own data
   *     is wrong, a rep needs to know for certain that nothing was re-fetched
   *     underneath them, not merely that their edit happened to win a merge.
   */
  const pullCredit = async ({ usePanelOnly = false, piiOverride = null, owner = 'primary' } = {}) => {
    setCreditPulling(true); setCreditResult(null);
    try {
      // piiOverride wins outright: it is used by the second-owner pull, which
      // has just set these same fields and cannot read them back out of state
      // in the same tick.
      let pii = piiOverride || {
        ssn: (creditSsn || '').replace(/\D/g, '').slice(0, 9),
        firstName: creditFirstName, lastName: creditLastName, dob: creditDob,
        streetLine1: creditStreet, city: creditCity, zip: creditZip,
      };
      if (!usePanelOnly && !piiOverride) {
        // preferExisting, so a corrected surname, SSN, DOB or address is what
        // actually gets pulled. Blanks are still filled from the application.
        // A prefill failure is not fatal - whatever is typed is worth pulling on.
        try { pii = await runPrefill({ silent: true, preferExisting: true }); }
        catch { /* fall back to what's typed */ }
      }
      if (pii.ssn.length !== 9) {
        throw new Error(usePanelOnly
          ? 'The SSN box needs 9 digits to pull with these fields.'
          : 'No SSN on this client — add it to Client Information or the application, then pull again');
      }
      const res = await fetch(`${API}/credit/pull`, {
        method: 'POST',
        headers,
        // owner tells the server whose report this is. Absent/'primary' is the
        // principal, exactly as every pull behaved before second owners.
        body: JSON.stringify({ clientId: id, ...pii, owner }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      // Robust score parse — strip any non-digits then validate 300-850
      const rawScore = data.score ? parseInt(String(data.score).replace(/\D/g, ''), 10) : null;
      const score = (rawScore && rawScore >= 300 && rawScore <= 850) ? rawScore : null;
      // scoreModel comes back from the pull ("VantageScore 4.0"), so the panel
      // can say which model the number is rather than only that it came from
      // Experian.
      // locked/label/meaning/nextStep ride along so the panel can say the file is
      // frozen instead of showing a tick and "no score returned", which is what a
      // frozen pull used to look like.
      setCreditResult({
        ok: true, score, scoreModel: data.scoreModel || null,
        locked: !!data.locked, outcome: data.code || null,
        label: data.label || null, meaning: data.meaning || null, nextStep: data.nextStep || null,
      });
      const pulledAt = data.pulledAt || new Date().toISOString();
      if (score) {
        // Persist score onto client record — experianScore overwrites stated creditScore
        await fetch(`${API}/clients-api/${id}`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ creditScore: score, experianScore: score, creditPulledAt: pulledAt }),
        }).catch(() => {});
        setApiClient(prev => ({ ...(prev || {}), creditScore: score, experianScore: score, creditPulledAt: pulledAt }));
        // Force-reload so score sticks in header after creditResult resets
        fetch(`${API}/clients-api/${id}`, { headers })
          .then(r => r.ok ? r.json() : null)
          .then(fresh => { if (fresh) setApiClient(fresh); })
          .catch(() => {});
      }
      // Retry doc refresh up to 3x to handle S3 write latency
      const refreshDocs = (attempt) => {
        setTimeout(() => {
          fetch(`${API}/documents/client/${id}`, { headers })
            .then(r => r.ok ? r.json() : [])
            .then(docs => {
              setRealDocs(docs);
              if (!docs.some(d => d.category === 'credit_report') && attempt < 3) refreshDocs(attempt + 1);
            })
            .catch(() => { if (attempt < 3) refreshDocs(attempt + 1); });
        }, attempt * 2500);
      };
      refreshDocs(1);
      if (data.locked) {
        setNotification(`🔒 ${data.label || 'Credit is locked'} — ${data.nextStep || 'the merchant has to lift the freeze with Experian.'}`);
        setTimeout(() => setNotification(null), 12000);
      } else {
        setNotification(`✅ Experian credit pulled — Score: ${score || (data.pdfKey ? 'See report' : 'No score returned')}`);
        setTimeout(() => setNotification(null), 5000);
      }
    } catch (e) {
      setCreditResult({ ok: false, error: e.message });
      setNotification(`❌ Credit pull failed: ${e.message}`);
      setTimeout(() => setNotification(null), 5000);
    }
    setCreditPulling(false);
  };

  // Sends this client's contact details to Matt through Power Automate once the
  // rep has said Yes in the confirmation.
  // The Zoho record this deal is associated with, by the same precedence the
  // "Find in Zoho" button uses: a link pinned by hand in Auto Submit's
  // Associated Link wins, then one a Zoho search already resolved and saved,
  // record id stored on the client.
  //
  // zohoUniqueId is only trusted when it looks like a Zoho id - on some records
  // it holds Orbit's own client UUID rather than a CRM id, and building a URL
  // out of that would hand Matt a link to nothing.
  const ZOHO_ORG_ID = '683020569';
  const associatedZohoLink = () => {
    const pinned = String(client?.zohoUrlOverride || '').trim();
    if (pinned) return pinned;
    // zohoUrl is what POST /zoho-crm/link wrote back after a confident
    // match, so a link resolved by anyone on any screen is the one Matt gets.
    const saved = String(client?.zohoUrl || client?.zohoLink || '').trim();
    if (saved) return saved;
    const recordId = [client?.zohoId, client?.zohoUniqueId]
      .map(v => String(v ?? '').trim())
      .find(v => /^\d{6,}$/.test(v));
    return recordId
      ? `https://crm.zoho.com/crm/org${ZOHO_ORG_ID}/tab/Accounts/${recordId}`
      : '';
  };

  // Renewal - copies this client (every Client Information field and every
  // document) into a new deal named "<name> - 2", or the next free number.
  // The deal's history (Zoho link, envelope, lender IDs, status) is not
  // copied - see POST /clients-api/:id/renewal. Opens the new deal when done.
  const handleRenewal = async () => {
    if (renewing) return;
    if (!window.confirm(`Create a renewal of "${client.businessName}"?\n\nAll client information and documents are copied into a new deal. The Zoho link, DocuSign envelope and lender history are not.`)) return;
    setRenewing(true);
    try {
      const res = await fetch(`${API}/clients-api/${id}/renewal`, { method: 'POST', headers: getHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d?.client?.id) throw new Error(d?.error || `HTTP ${res.status}`);
      const failed = Array.isArray(d.failedDocs) ? d.failedDocs : [];
      // Submission/Auto/Journey Apps are rebuilt with today's signature date;
      // if that couldn't run, the original's were copied as they were.
      const redated = d.appsRedated?.ok
        ? '; Submission, Auto and Journey Apps re-dated to today'
        : `; apps NOT re-dated (${d.appsRedated?.reason || 'unknown'}) - use Recreate Apps on the Client Information tab`;
      setNotification(`Renewal created: ${d.client.businessName} (${d.copiedDocs} document${d.copiedDocs === 1 ? '' : 's'} copied${redated}${failed.length ? `; could not copy ${failed.join(', ')}` : ''})`);
      setTimeout(() => setNotification(null), 6000);
      navigate(`/clients/${d.client.id}`);
    } catch (e) {
      setNotification(`Renewal failed: ${e.message}`);
      setTimeout(() => setNotification(null), 6000);
    } finally {
      setRenewing(false);
    }
  };

  const handlePingMatt = async () => {
    setShowPingConfirm(false);
    if (!PING_MATT_URL) {
      setNotification('Ping is not set up yet - no Power Automate URL configured.');
      setTimeout(() => setNotification(null), 4000);
      return;
    }
    setPinging(true);
    try {
      const res = await fetch(PING_MATT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: client.businessName || '',
          clientName:   client.ownerName    || '',
          email:        client.email        || '',
          phone:        client.phone || client.tabData?.phone || client.tabData?.phonenumber || client.tabData?.['phone number'] || client.tabData?.['phone_number'] || '',
          assignedRep:  client.assignedRepName || rep?.name || '',
          clientId:     id,
          pingedBy:     user?.full_name || user?.email || '',
          clientUrl:    `${FRONTEND_URL}${window.location.pathname}`,
          // Empty string, not omitted, when the deal has no Zoho record linked
          // yet - the flow gets the same shape either way.
          zohoLink:     associatedZohoLink(),
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setNotification('Matt has been pinged');
    } catch (e) {
      setNotification(`Ping failed: ${e.message}`);
    }
    setTimeout(() => setNotification(null), 4000);
    setPinging(false);
  };

  // Refresh docs after upload
  const handleUploadComplete = () => {
    setTimeout(() => {
      fetch(`${API}/documents/client/${id}`, { headers: getHeaders() })
        .then(r => r.ok ? r.json() : [])
        .then(docs => setRealDocs(docs))
        .catch(() => {});
    }, 1500);
  };

  // Permanently removes every document this client has sitting in Trash - the
  // S3 objects and the index rows both go, for real, in one request. Confirmed
  // up front since there's no undo once it's run (matching the single-document
  // "Permanently Delete" confirmation elsewhere in the app).
  const handleEmptyTrash = async () => {
    const trashedCount = allDocs.filter(d => d.category === 'trash').length;
    if (!trashedCount) return;
    if (!window.confirm(`Permanently delete all ${trashedCount} file${trashedCount !== 1 ? 's' : ''} in Trash? This cannot be undone.`)) return;
    setEmptyingTrash(true);
    try {
      const res = await fetch(`${API}/documents/client/${id}/trash`, {
        method: 'DELETE', headers: getHeaders(),
        body: JSON.stringify({ performedBy: user?.full_name || user?.email || 'Unknown' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Server error');
      setRealDocs(prev => prev.filter(d => d.category !== 'trash'));
      setNotification(`??? Emptied Trash � ${data.deleted} file${data.deleted !== 1 ? 's' : ''} removed`);
      setTimeout(() => setNotification(null), 4000);
    } catch (e) {
      alert(`Could not empty Trash: ${e.message}`);
    } finally {
      setEmptyingTrash(false);
    }
  };

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto">
      {/* Notification */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3 shadow-lg">
          <CheckCircle2 size={16} className="text-green-600 shrink-0" />
          <p className="text-green-700 text-sm font-medium">{notification}</p>
          <button onClick={() => setNotification(null)} className="text-green-400 ml-2"><X size={14} /></button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Link to="/clients" className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-700 transition-colors">
            <ArrowLeft size={16} />
          </Link>
          <div>
            {dealNameDraft !== null ? (
              <input
                autoFocus
                value={dealNameDraft}
                disabled={dealNameSaving}
                maxLength={200}
                onChange={e => setDealNameDraft(e.target.value)}
                onBlur={saveDealName}
                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setDealNameDraft(null); }}
                className="text-2xl font-semibold text-gray-900 tracking-tight bg-white border border-blue-300 rounded-lg px-2 py-0.5 w-[32rem] max-w-full focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            ) : (
              <h1
                className={`text-2xl font-semibold text-gray-900 tracking-tight ${canEditDealName ? 'cursor-text' : ''}`}
                title={canEditDealName ? 'Double-click to rename the deal' : undefined}
                onDoubleClick={canEditDealName ? () => setDealNameDraft(client.businessName || '') : undefined}
              >{client.businessName}</h1>
            )}
            <p className="text-gray-400 text-sm">{client.ownerName} · {client.industry}</p>
          </div>
        </div>
        {/* The Zoho record sits in the header rather than inside the
            admin-only credit panel: finding the matching CRM deal is the first
            thing anyone does with a client, rep or admin, and it was three
            clicks down a panel most roles cannot even see. */}
        <div className="flex items-center gap-2 shrink-0 pt-1">
          {/* Admins only - super and ultra admins hold the admin role too. */}
          {user?.role === 'admin' && (
            <button type="button" onClick={handleRenewal} disabled={renewing}
              title="Copy this client and all its documents into a new deal named with the next number (- 2, - 3, ...)"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors">
              <RefreshCw size={12} className={renewing ? 'animate-spin' : ''} />
              {renewing ? 'Creating…' : 'Renewal'}
            </button>
          )}
          <ZohoLink
            clientId={id}
            client={client}
            onSaved={(u) => setApiClient(prev => ({ ...(prev || {}), ...u }))}
          />
          <StatusBadge status={client.status} />
        </div>
      </div>

      {/* Quick-jump client search - same box and matching rules as Clients.jsx
          (see utils/clientSearch.js), so a rep can find another client from
          inside this one instead of going back to the list first. Results
          close on blur (with a short delay so a click on a result registers
          before the blur removes it) and on Escape. */}
      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={jumpQuery}
          onChange={e => { setJumpQuery(e.target.value); setJumpOpen(true); }}
          onFocus={() => { loadJumpClients(); setJumpOpen(true); }}
          onBlur={() => setTimeout(() => setJumpOpen(false), 150)}
          onKeyDown={e => { if (e.key === 'Escape') { e.target.blur(); setJumpOpen(false); } }}
          placeholder="Search clients..."
          className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-sm transition-all"
        />
        {jumpOpen && jumpQuery.trim() && (
          <div className="absolute z-20 top-full left-0 right-0 mt-1.5 bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden">
            {jumpLoading ? (
              <p className="px-4 py-3 text-sm text-gray-400">Loading clients…</p>
            ) : jumpResults.length ? (
              jumpResults.map(c => (
                <button
                  key={c.id}
                  type="button"
                  // onMouseDown, not onClick, so it still fires before the
                  // input's onBlur closes the dropdown. This used to be a
                  // <Link>, but closing the dropdown from inside its own
                  // onMouseDown unmounted the <Link> before the browser ever
                  // dispatched the "click" React Router listens for - the
                  // element was gone by the time click would have fired, so
                  // nothing happened. Navigating here directly (instead of
                  // through Link's click handling) doesn't have that
                  // dependency on the element still existing afterward.
                  onMouseDown={() => { setJumpQuery(''); setJumpOpen(false); navigate(`/clients/${c.id}`); }}
                  className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0 text-left"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{c.businessName || c.ownerName || '—'}</p>
                    <p className="text-xs text-gray-400 truncate">{c.ownerName}{c.email ? ` · ${c.email}` : ''}</p>
                  </div>
                  <StatusBadge status={c.status} />
                </button>
              ))
            ) : (
              <p className="px-4 py-3 text-sm text-gray-400">No clients match "{jumpQuery}"</p>
            )}
          </div>
        )}
      </div>

      {/* Nothing downstream � underwriting, OCR, lender submission � can run
          without statements, so this sits above the tabs rather than inside
          Documents, where a rep only looks once they already suspect a gap.
          Held back until the docs fetch settles to avoid flashing on load. */}
      {!loadingDocs && !hasBankStatements && (
        <button
          onClick={() => { setActiveTab('Documents'); setSelectedCategory('bank_statements'); }}
          className="w-full flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-left hover:bg-amber-100/70 transition-colors"
        >
          <AlertCircle size={16} className="text-amber-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-amber-900 text-sm font-medium">No bank statements uploaded</p>
            <p className="text-amber-700 text-xs">Underwriting and lender submission need statements on file.</p>
          </div>
          <span className="flex items-center gap-1 text-amber-700 text-xs font-semibold shrink-0">
            Upload <ChevronRight size={13} />
          </span>
        </button>
      )}

      {/* Info strip */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { icon: Mail, value: client.email || '—' },
          { icon: Phone, value: client.phone || client.tabData?.phone || client.tabData?.phonenumber || client.tabData?.['phone number'] || client.tabData?.['phone_number'] || '—' },
          { icon: MapPin, value: client.state || client.tabData?.state || client.tabData?.st || client.address || client.tabData?.address || client.tabData?.city || '—' },
          // Same rule as the Puller column on the Clients list: the puller set
          // on the record, else the rep the client was first created under.
          { icon: UserCheck, value: `Puller: ${client.pullerName || (client.originalRepName && client.originalRepName !== 'Unassigned' ? client.originalRepName : '') || '—'}` },
          { icon: Building2, value: `Rep: ${client.assignedRepName || rep?.name || '—'}` },
        ].map((f, i) => (
          <div key={i} className="flex items-center gap-2 text-gray-600 text-sm">
            <f.icon size={13} className="text-gray-400 shrink-0" />
            <span className="truncate">{f.value}</span>
          </div>
        ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-100 overflow-x-auto">
        {TABS.filter(tab => can.seeSeasonalityUnderwriting || !['Seasonality', 'Underwriting'].includes(tab)).map(tab => {
          const isIncompleteInfo = tab === 'Client Information' && missingClientInfoCount > 0;
          const isIncompleteDocs = tab === 'Documents' && missingDocRequirementsCount > 0;
          const isFlagged = isIncompleteInfo || isIncompleteDocs;
          return (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${
              activeTab === tab
                ? isFlagged ? 'border-red-600 text-red-600' : 'border-blue-600 text-blue-600'
                : isFlagged ? 'border-transparent text-red-500 hover:text-red-600' : 'border-transparent text-gray-400 hover:text-gray-700'
            }`}
          >
            {t(tab)}
            {tab === 'Documents' && (
              isIncompleteDocs ? (
                <span className="ml-1.5 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-semibold">
                  {missingDocRequirementsCount}
                </span>
              ) : (
                <span className="ml-1.5 text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">
                  {allDocs.length}
                </span>
              )
            )}
            {isIncompleteInfo && (
              <span className="ml-1.5 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-semibold">
                {missingClientInfoCount}
              </span>
            )}
          </button>
          );
        })}
      </div>

      {/* Documents Tab */}
      {activeTab === 'Documents' && (
        <div>
          <div className="flex justify-end gap-2 mb-3">
            <button onClick={() => setShowPingConfirm(true)} disabled={pinging}
              className="flex items-center gap-1.5 text-xs bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg font-medium transition-colors disabled:opacity-50">
              <Bell size={13} />
              {pinging ? 'Pinging...' : 'Ping'}
            </button>
            <a href={`${API}/documents/client/${id}/download-all`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg font-medium transition-colors">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
              Download All
            </a>
          </div>
          {showPingConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" onClick={() => setShowPingConfirm(false)}>
              <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
                <p className="text-gray-900 text-base font-semibold text-center">Would You Like to Ping Matt?</p>
                <div className="flex justify-center gap-3 mt-5">
                  <button onClick={() => setShowPingConfirm(false)}
                    className="text-sm bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-5 py-2 rounded-lg font-medium transition-colors">
                    No
                  </button>
                  <button onClick={handlePingMatt}
                    className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-medium transition-colors">
                    Yes
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Missing documents and Custom Request live here rather than
              above the tabs: they are about documents, and a rep acts on
              them in the same place they upload and review files. */}
          <div className="space-y-4 mb-4">
          {/* Missing docs alert with one-click request */}
          {missingCategories.length > 0 && (
            <div className="bg-white border border-red-100 rounded-2xl shadow-sm overflow-hidden">
              {/* The whole bar is the control, not just the arrow - a rep
                  aiming at a 14px chevron on a touch screen is a rep who
                  misses it. Same pattern as Custom Request below. */}
              <button
                type="button"
                onClick={() => setShowMissingDocs(v => !v)}
                aria-expanded={showMissingDocs}
                className={`w-full flex items-center justify-between gap-2 px-5 py-3.5 bg-red-50 hover:bg-red-100/70 transition-colors ${showMissingDocs ? 'border-b border-red-100' : ''}`}>
                <div className="flex items-center gap-2">
                  <AlertCircle size={15} className="text-red-500 shrink-0" />
                  <p className="text-red-600 text-sm font-medium">
                    {missingCategories.length} Missing Document{missingCategories.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-red-400 text-xs font-medium">
                    {showMissingDocs ? 'Hide' : 'Show'}
                  </span>
                  <ChevronDown size={18}
                    className={`text-red-500 transition-transform duration-200 ${showMissingDocs ? 'rotate-180' : ''}`} />
                </div>
              </button>
              <div className={`divide-y divide-gray-50 ${showMissingDocs ? '' : 'hidden'}`}>
                {missingCategories.map(cat => {
                  const reqState = requestingSent[cat.id];
                  return (
                    <div key={cat.id} className="flex items-center justify-between px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <span className="text-base">{cat.icon}</span>
                        <span className="text-gray-700 text-sm font-medium">{cat.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-red-400 font-medium">Missing</span>
                        {client.email && reqState !== 'sent' && !['credit_report','admin_app','admin_app_ne'].includes(cat.id) && (
                          <button
                            onClick={() => sendDocRequest(cat.id)}
                            disabled={reqState === 'sending'}
                            className="flex items-center gap-1 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-2.5 py-1.5 rounded-lg transition-colors font-medium"
                          >
                            <Send size={11} /> {reqState === 'sending' ? 'Sending...' : 'Request'}
                          </button>
                        )}
                        {reqState === 'sent' && (
                          <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                            <CheckCircle2 size={11} /> Sent
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Custom Request Bar */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <button onClick={() => setShowCustomRequest(v => !v)} className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-2.5">
                <Bell size={15} className="text-blue-600" />
                <span className="text-gray-900 text-sm font-medium">Custom Request</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-xs">Send a document request to this client</span>
                <Plus size={14} className={`text-gray-400 transition-transform ${showCustomRequest ? 'rotate-45' : ''}`} />
              </div>
            </button>
            {showCustomRequest && (
              <form onSubmit={sendCustomRequest} className="px-5 pb-5 pt-2 border-t border-gray-100 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Document Type</label>
                    <select required value={customReqForm.category} onChange={e => setCustomReqForm(f => ({ ...f, category: e.target.value }))} className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                      <option value="">Select...</option>
                      {DOC_CATEGORIES.filter(c => !['credit_report','admin_app','admin_app_ne','admin_app_journey'].includes(c.id)).map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Due Date</label>
                    <input type="date" value={customReqForm.dueDate} onChange={e => setCustomReqForm(f => ({ ...f, dueDate: e.target.value }))} className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                  <div className="flex items-end">
                    <button type="submit" disabled={sendingCustom || !customReqForm.category} className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm px-4 py-2.5 rounded-xl font-medium transition-colors w-full justify-center">
                      <Send size={13} /> {sendingCustom ? 'Sending...' : 'Send Request'}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Instructions (optional)</label>
                  <textarea value={customReqForm.instructions} onChange={e => setCustomReqForm(f => ({ ...f, instructions: e.target.value }))} rows={2} className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none" placeholder="Describe what you need from the client..." />
                </div>
              </form>
            )}
          </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-5">
          {/* Folder tree — shows real doc counts */}
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-3">
            <p className="text-gray-400 text-xs font-medium uppercase tracking-wider px-2 mb-2">Folders</p>
            <div className="space-y-0.5">
              {/* Subfolders (DOC_CATEGORIES entries with a `parent`) are not
                  listed here - they open from inside their parent folder's
                  panel, and the parent stays highlighted while one is open. */}
              {DOC_CATEGORIES.filter(cat => !cat.parent && !(['admin_app','admin_app_ne'].includes(cat.id) && !can.seeInternalDocs)).map(cat => {
                const count = allDocs.filter(d => d.category === cat.id).length;
                const active = selectedCategory === cat.id || selectedCategoryRoot === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-all ${
                      active ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'
                    }`}
                  >
                    <span className="text-xs font-medium truncate">{cat.icon} {cat.label}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                      count > 0 ? 'bg-gray-100 text-gray-500' : cat.required ? 'bg-red-50 text-red-400' : 'bg-gray-50 text-gray-300'
                    }`}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* File panel */}
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
              <div>
                {selectedCategoryAncestors.length > 0 ? (
                  <p className="text-sm font-semibold flex flex-wrap items-center gap-1.5">
                    {selectedCategoryAncestors.map(a => (
                      <React.Fragment key={a.id}>
                        <button onClick={() => setSelectedCategory(a.id)}
                          className="text-blue-600 hover:underline">
                          {a.label}
                        </button>
                        <span className="text-gray-300">/</span>
                      </React.Fragment>
                    ))}
                    <span className="text-gray-900">{catLabel}</span>
                  </p>
                ) : (
                  <p className="text-gray-900 font-semibold text-sm">{catLabel}</p>
                )}
                <p className="text-gray-400 text-xs">
                  {loadingDocs ? t('Loading...') : `${visibleDocs.length} ${visibleDocs.length !== 1 ? t('files') : t('file')}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {selectedCategory === 'bank_statements' && (
                  <>
                    <select
                      value={selectedBankType}
                      onChange={e => { setSelectedBankType(e.target.value); setOcrBankStatus(''); }}
                      className="text-xs bg-gray-50 border border-gray-200 text-gray-700 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      <option value="">{t('Select bank type...')}</option>
                      {SUPPORTED_BANKS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                    </select>
                    {/* Send to OCR - commented out for now per request. Left
                        in place rather than deleted so it can come back with
                        a one-line uncomment; nothing else here depends on it
                        (bankDocs/ocrBankSending/ocrBankStatus/the /ocr/send-to-go
                        call all live inside this one block). */}
                    {false && (
                    <button
                      onClick={async () => {
                        if (selectedBankType === 'other') { setOcrBankStatus(t('OCR Unsupported')); return; }
                        const bankDocs = allDocs.filter(d => d.category === 'bank_statements' && d.status !== 'Deleted');
                        if (bankDocs.length === 0) { setOcrBankStatus('No bank statements to send'); return; }
                        setOcrBankSending(true); setOcrBankStatus(t('Sending...'));
                        try {
                          const res = await fetch(`${API}/ocr/send-to-go`, {
                            method: 'POST',
                            headers,
                            body: JSON.stringify({ clientId: id, bankType: selectedBankType || 'manual' }),
                          });
                          const data = await res.json();
                          if (!res.ok) throw new Error(data.error || 'Failed');
                          setOcrBankStatus(t('Sent!'));
                        } catch (e) { setOcrBankStatus(`Error: ${e.message}`); }
                        setOcrBankSending(false);
                      }}
                      disabled={ocrBankSending}
                      className="flex items-center gap-1 text-xs bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg transition-colors font-medium"
                    >
                      <Send size={11} /> {ocrBankSending ? t('Sending...') : t('Send to OCR')}
                    </button>
                    )}
                    {false && ocrBankStatus && <span className={`text-xs font-medium ${ocrBankStatus.includes('Error') || ocrBankStatus.includes('OCR') ? 'text-amber-600' : 'text-green-600'}`}>{ocrBankStatus}</span>}
                    <button
                      onClick={addBankAccount}
                      className="flex items-center gap-1 text-xs text-gray-500 bg-gray-50 hover:bg-gray-100 border border-gray-200 px-2.5 py-1.5 rounded-lg transition-colors font-medium"
                    >
                      <Plus size={11} /> {t('Add Account')}
                    </button>
                  </>
                )}
                {selectedCategory === 'trash' && can.uploadForClient && visibleDocs.length > 0 && (
                  <button
                    onClick={handleEmptyTrash}
                    disabled={emptyingTrash}
                    className="flex items-center gap-1.5 text-xs bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg transition-colors font-medium"
                  >
                    <Trash2 size={13} /> {emptyingTrash ? t('Deleting...') : t('Delete All')}
                  </button>
                )}
                {selectedCategory !== 'bank_statements' && can.uploadForClient && (
                  <button
                    onClick={() => setShowUpload(v => !v)}
                    className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors font-medium"
                  >
                    <Upload size={13} /> {t('Upload')}
                  </button>
                )}
                {selectedCategory === 'bank_statements' && can.uploadForClient && (
                  <button
                    onClick={() => setShowUpload(v => !v)}
                    className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors font-medium"
                  >
                    <Upload size={13} /> {t('Upload')}
                  </button>
                )}
              </div>
            </div>

            {showUpload && (
              <div className="p-4 border-b border-gray-100 bg-gray-50">
                <UploadZone
                  category={selectedCategory}
                  categoryLabel={catLabel}
                  clientId={id}
                  uploadedBy={user?.id}
                  onUpload={handleUploadComplete}
                  defaultBankAccount={selectedCategory === 'bank_statements' ? (selectedBankAccount || 'Account 1') : null}
                  compact
                  tall
                />
              </div>
            )}

            {/* Subfolders of the open folder (e.g. Equipment/Invoice inside
                Additional Submission Docs), listed above its own files. */}
            {DOC_CATEGORIES.filter(c => c.parent === selectedCategory).map(sub => {
              const subCount = allDocs.filter(d => d.category === sub.id).length;
              return (
                <button key={sub.id} onClick={() => setSelectedCategory(sub.id)}
                  className="w-full flex items-center justify-between px-5 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors text-left">
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <span className="text-base">📁</span> {sub.icon} {sub.label}
                  </span>
                  <span className="flex items-center gap-2 text-xs text-gray-400">
                    {subCount} {subCount === 1 ? t('file') : t('files')}
                    <ChevronRight size={14} />
                  </span>
                </button>
              );
            })}

            {selectedCategory === 'bank_statements' && allBankAccounts.length > 0 && (
              <div className="flex items-center gap-1 px-5 py-2 border-b border-gray-50 bg-gray-50/50 overflow-x-auto">
                <button
                  onClick={() => setSelectedBankAccount('')}
                  className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap ${!selectedBankAccount ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  All
                </button>
                {allBankAccounts.map(a => (
                  <button
                    key={a}
                    onClick={() => setSelectedBankAccount(a)}
                    className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap ${selectedBankAccount === a ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            )}

            <DocumentTable
              documents={visibleDocs}
              canChangeStatus={can.seeInternalDocs}
              onMove={(docId, newCategory) => setRealDocs(prev => prev.map(d => d.id === docId ? { ...d, category: newCategory } : d))}
              onRename={(docId, fileName) => setRealDocs(prev => prev.map(d => d.id === docId ? { ...d, fileName } : d))}
            />
          </div>
        </div>
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === 'Requests' && (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm divide-y divide-gray-50">
          {pendingRequests.length === 0 ? (
            <p className="text-gray-400 text-sm px-5 py-6 text-center">No pending requests. Use the Custom Request bar above to send one.</p>
          ) : pendingRequests.map(req => {
            const cat = DOC_CATEGORIES.find(c => c.id === req.category);
            return (
              <div key={req.id} className="px-5 py-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-gray-800 font-medium text-sm">{cat?.icon} {req.catLabel || cat?.label}</p>
                    {req.instructions && <p className="text-gray-400 text-xs mt-1">{req.instructions}</p>}
                    <p className="text-gray-300 text-xs mt-1.5 flex items-center gap-1">
                      <Clock size={11} /> {req.dueDate ? `Due ${fmt(req.dueDate)}` : `Sent ${fmt(req.createdAt)}`}
                    </p>
                  </div>
                  <button onClick={() => fulfillRequest(req.id)} className="flex items-center gap-1 text-xs text-green-600 hover:bg-green-50 border border-green-200 px-2.5 py-1.5 rounded-lg transition-colors">
                    <CheckCircle2 size={11} /> Mark Done
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Activity Tab */}
      {activeTab === 'Activity' && (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm divide-y divide-gray-50">
          {clientActivity.length === 0 ? (
            <p className="text-gray-400 text-sm px-5 py-6 text-center">No activity recorded for this client yet.</p>
          ) : clientActivity.map(a => {
            const typeColor = {
              upload: 'text-blue-600 bg-blue-50',
              login: 'text-green-600 bg-green-50',
              status_change: 'text-green-600 bg-green-50',
              request: 'text-amber-600 bg-amber-50',
              note: 'text-purple-600 bg-purple-50',
            }[a.eventType] || 'text-gray-500 bg-gray-100';
            return (
              <button key={a.id} onClick={() => { if (a.eventType === 'upload') setActiveTab('Documents'); }} className={`w-full flex items-start gap-3 px-5 py-3.5 text-left ${a.eventType === 'upload' ? 'hover:bg-blue-50/40 cursor-pointer' : ''} transition-colors`}>
                <span className={`text-xs font-medium px-2 py-1 rounded-lg shrink-0 mt-0.5 ${typeColor}`}>
                  {(a.eventType || '').replace('_', ' ')}
                </span>
                <div className="flex-1">
                  <p className="text-gray-700 text-sm">{a.description}</p>
                  <p className="text-gray-400 text-xs mt-0.5">{fmt(a.timestamp || a.createdAt)}</p>
                </div>
                {a.eventType === 'upload' && <ChevronRight size={14} className="text-gray-300 shrink-0 mt-1" />}
              </button>
            );
          })}
        </div>
      )}

      {/* Notes Tab */}
      {activeTab === 'Notes' && (
        <div className="space-y-3">
          {allDocs.filter(d => d.visibility === 'internal' && d.note).map(d => (
            <div key={d.id} className="bg-white border border-gray-100 rounded-2xl shadow-sm px-5 py-4">
              <p className="text-gray-700 text-sm leading-relaxed">{d.note}</p>
              <p className="text-gray-400 text-xs mt-2">{d.fileName} · {fmt(d.uploadedAt)}</p>
            </div>
          ))}
          {allDocs.filter(d => d.visibility === 'internal' && d.note).length === 0 && (
            <p className="text-gray-400 text-sm">No internal notes.</p>
          )}
        </div>
      )}

      {/* Client Information Tab */}
      {activeTab === 'Client Information' && (
        <ClientInformationTab
          client={client}
          id={id}
          headers={headers}
          // Link Data derives Portal Monthly / Yearly Rev from the average
          // monthly revenue the statements give, so it needs the same figures
          // the Underwriting tab reports.
          docFinancials={docFinancials}
          onSaved={(updated) => setApiClient(prev => ({ ...(prev || {}), ...updated }))}
          // Recreate Apps wrote two new documents - refresh the list so they show.
          onAppsRecreated={handleUploadComplete}
        />
      )}

      {/* Seasonality Tab */}
      {activeTab === 'Seasonality' && (
        <SeasonalityTab statementTimeline={statementTimeline} t={t} />
      )}

      {/* Underwriting Tab */}
      {activeTab === 'Underwriting' && (
        <UnderwritingTab
          id={id}
          token={token}
          headers={headers}
          realDocs={realDocs}
          docFinancials={docFinancials}
          statementTimeline={statementTimeline}
          ocrResults={ocrResults}
          setOcrResults={setOcrResults}
          ocrSending={ocrSending}
          setOcrSending={setOcrSending}
          ocrStatus={ocrStatus}
          setOcrStatus={setOcrStatus}
        />
      )}

      {/* Auto Submit Tab */}
      {activeTab === 'Auto Submit' && (
        <div className="space-y-4">
          {/* Credit sits at the top of Auto Submit, where a pull is
              actually needed, instead of in the header strip on every
              tab. Still admin-only - can.seeCreditPanel is unchanged. */}
          {can.seeCreditPanel && (
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm px-4 pt-3 pb-1">
              {/* Credit toggle header */}
              <button
                onClick={() => setShowCreditPanel(v => !v)}
                className="w-full flex items-center justify-between py-1.5 group"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Credit</span>
                  {(() => {
                    // Always prefer live creditResult, then apiClient scores, then mockClient scores
                    const displayed = creditResult?.ok ? creditResult.score
                      : (apiClient?.experianScore || apiClient?.creditScore || client.experianScore || client.creditScore);
                    const isExperian = !!(creditResult?.ok || apiClient?.experianScore || client.experianScore);
                    // Name the model when the pull told us one - a rep asking
                    // "is this a FICO?" can read the answer instead of guessing
                    // from the number.
                    const source = creditResult?.ok && creditResult.scoreModel
                      ? `· ${creditResult.scoreModel}`
                      : (isExperian ? '· Experian' : '· Stated');
                    // Whose score each number is. Only worth printing once
                    // there are two of them - on a one-owner deal a name in
                    // brackets beside the only score is noise.
                    const principalName = String(client.ownerName || '').trim();
                    const secondName = String(secondOwnerReport?.ownerName || client.secondaryOwnerName || '').trim();
                    const named = (n) => (secondOwnerReport && n ? ` (${n})` : '');
                    return displayed ? (
                      <span className="text-xs font-bold text-indigo-600">
                        {displayed}
                        <span className="text-gray-400 font-normal ml-1">{source}</span>
                        <span className="text-gray-400 font-normal">{named(principalName)}</span>
                        {!creditResult?.ok && (apiClient?.creditPulledAt || client.creditPulledAt) && (
                          <span className="text-gray-400 font-normal ml-1">· {fmt(apiClient?.creditPulledAt || client.creditPulledAt)}</span>
                        )}
                        {secondOwnerReport && (
                          <span className="ml-2 text-purple-700">
                            {secondOwnerReport.score}
                            <span className="text-gray-400 font-normal ml-1">
                              · {secondOwnerReport.scoreModel || 'Experian'}{named(secondName)}
                            </span>
                          </span>
                        )}
                      </span>
                    ) : secondOwnerReport ? (
                      // The second owner pulled but the principal never was.
                      <span className="text-xs font-bold text-purple-700">
                        {secondOwnerReport.score}
                        <span className="text-gray-400 font-normal ml-1">
                          · {secondOwnerReport.scoreModel || 'Experian'}{secondName ? ` (${secondName})` : ''}
                        </span>
                      </span>
                    ) : null;
                  })()}
                  {creditResult?.ok === false && <span className="text-xs text-red-500">{creditResult.error}</span>}
                  {/* Frozen file. On the header so it is visible without opening the
                      panel, and from creditLock as well as the live pull so it
                      survives a refresh. */}
                  {(creditResult?.locked || creditLock) && (
                    <span className="text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300 rounded px-1.5 py-0.5">
                      CREDIT LOCKED
                    </span>
                  )}
              </div>
                <ChevronRight size={14} className={`text-gray-400 transition-transform ${showCreditPanel ? 'rotate-90' : ''}`} />
              </button>
              {(creditResult?.locked || creditLock) && (
                <div className="mb-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2">
                  <div className="text-xs font-semibold text-amber-900">
                    {creditResult?.label || creditLock?.reason || 'This merchant has frozen their credit file'}
                  </div>
                  <div className="text-[11px] text-amber-800 mt-0.5 leading-relaxed">
                    {creditResult?.meaning || 'Experian returned no score because the file is frozen.'}
                  </div>
                  <div className="text-[11px] text-amber-900 mt-1 font-medium">
                    {creditResult?.nextStep
                      || 'The merchant has to lift the freeze with Experian. Re-pulling before they do returns this same answer.'}
                  </div>
                  {creditLock?.pulledAt && !creditResult?.locked && (
                    <div className="text-[10px] text-amber-700 mt-1">Last pull: {fmt(creditLock.pulledAt)}</div>
                  )}
                </div>
              )}

              {/* Collapsible credit panel - whole section is admin-only (see
                  can.seeCreditPanel above), so no further gate needed here */}
              {showCreditPanel && (
                <div className="mt-2 space-y-2 pb-2">
                  <div className="flex items-center justify-between">
                    <button onClick={prefillFromApplication} disabled={creditPrefilling}
                      className="flex items-center gap-1 text-xs bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 px-2.5 py-1 rounded-lg font-medium transition-colors disabled:opacity-50">
                      {creditPrefilling ? 'Prefilling…' : '⚡ Prefill from Application'}
                    </button>
                </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" value={creditFirstName} onChange={e => setCreditFirstName(e.target.value)} placeholder="First Name"
                      className="bg-gray-50 border border-gray-200 text-gray-900 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
                    <input type="text" value={creditLastName} onChange={e => setCreditLastName(e.target.value)} placeholder="Last Name"
                      className="bg-gray-50 border border-gray-200 text-gray-900 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
                    <input type="text" value={creditDob} onChange={e => setCreditDob(e.target.value)} placeholder="DOB (MM/DD/YYYY)"
                      className="bg-gray-50 border border-gray-200 text-gray-900 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
                    <input type="password" value={creditSsn} onChange={e => setCreditSsn(e.target.value.replace(/\D/g, '').slice(0, 9))} placeholder="SSN (9 digits)" maxLength={9}
                      className="bg-gray-50 border border-gray-200 text-gray-900 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
                    <input type="text" value={creditStreet} onChange={e => setCreditStreet(e.target.value)} placeholder="Street Address"
                      className="bg-gray-50 border border-gray-200 text-gray-900 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
                    <div className="flex gap-1">
                      <input type="text" value={creditCity} onChange={e => setCreditCity(e.target.value)} placeholder="City"
                        className="flex-1 bg-gray-50 border border-gray-200 text-gray-900 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
                      <input type="text" value={creditZip} onChange={e => setCreditZip(e.target.value)} placeholder="Zip" maxLength={5}
                        className="w-16 bg-gray-50 border border-gray-200 text-gray-900 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
                  </div>
                </div>
                  <div className="flex items-center gap-2 pt-1">
                    {/* No longer gated on a 9-digit SSN already being in the
                        box: the click prefills first, so requiring the SSN up
                        front disabled the one button that would have gone and
                        fetched it. A client with no SSN anywhere still gets
                        told so, from pullCredit's own check, instead of a dead
                        button with no explanation. */}
                    <button onClick={() => pullCredit()} disabled={creditPulling}
                      title="Fill anything blank from the application, keep what you have typed, then pull"
                      className="flex items-center gap-1.5 text-xs bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg font-medium transition-colors">
                      {creditPulling ? 'Pulling…' : '📊 Pull Credit'}
                    </button>
                    {/* Pulls exactly what is in the boxes above, without calling
                        the prefill at all. "It kept what I typed" and "it never
                        went and looked" are different guarantees: when the
                        application's own data is wrong, a rep needs to know
                        nothing was re-fetched underneath them rather than trust
                        that their edit won a merge. Only offered once there is a
                        usable SSN in the box, since that is the one field this
                        path cannot go and find. */}
                    {/* Loads the second owner into the fields above; see
                        fillFromSecondOwner on why it stops short of pulling.
                        ALWAYS rendered, disabled when this deal has no second
                        owner, rather than hidden: only 34 of 5,241 clients name
                        one, so hiding it meant the button was invisible on
                        essentially every deal and read as missing. */}
                    <button
                      onClick={pullSecondOwnerCredit}
                      disabled={creditPulling || creditPrefilling || !secondOwnerName}
                      title={secondOwnerName
                        ? `Load ${secondOwnerName}'s name, DOB, SSN and address into the fields above and pull their credit`
                        : 'This deal has no second owner — add one under Secondary Owner on Client Information'}
                      className="flex items-center gap-1.5 text-xs bg-white border border-purple-300 text-purple-700 hover:bg-purple-50 disabled:opacity-40 disabled:border-gray-200 disabled:text-gray-400 px-3 py-1.5 rounded-lg font-medium transition-colors">
                      {creditPulling ? 'Pulling…' : '👥 Pull 2nd owner credit'}
                    </button>
                    <button
                      onClick={() => pullCredit({ usePanelOnly: true })}
                      disabled={creditPulling || creditSsn.replace(/\D/g, '').length !== 9}
                      title={creditSsn.replace(/\D/g, '').length !== 9
                        ? 'Needs a 9-digit SSN in the box above'
                        : 'Pull using exactly the fields above, without re-reading the application'}
                      className="flex items-center gap-1.5 text-xs bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-40 px-3 py-1.5 rounded-lg font-medium transition-colors">
                      {creditPulling ? 'Pulling…' : '✎ Pull with these fields'}
                    </button>
                    {/* Same component as the header, so the two cannot
                        disagree about which record this deal is. */}
                    <ZohoLink clientId={id} client={client} onSaved={(u) => setApiClient(prev => ({ ...(prev || {}), ...u }))} compact />
                  </div>
                </div>
              )}
            </div>
          )}
          <Suspense fallback={<div className="p-4 text-gray-400 text-sm">Loading...</div>}>
            <AutoSubmit preselectedClientId={id} preselectedClient={client} autoOpenAndPrefill={openAutoSubmit} skipPreview={autoSubmitEditOnly} onClientInfoUpdated={(updated) => setApiClient(prev => ({ ...(prev || {}), ...updated }))} />
          </Suspense>
        </div>
      )}

      {activeTab === 'Audit Trail' && (
        <AuditTrail clientId={id} />
      )}

    </div>
  );
}
import AuditTrail from './AuditTrail';
